/// <amd-module name="Wit/Common/BacklogContent/BacklogContentView" />
import * as React from "react";
import { HubError } from "Agile/Common/Components/HubError/HubError";
import { IIterationData } from "Agile/Contracts/Common";
import { getTFSPageData, getTFSProject, ITeamData } from "Tfs/Platform/Page";
import { IObservableValue, ObservableArray, ObservableCollection, ObservableValue, useSubscription } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { getMenuItems, IExternalMenuItem } from "VSS/Features/PlatformUI/ContributedMenu";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { localeDateFormat } from "VSS/Platform/Date";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, IVssLayoutManager } from "VSS/Platform/Layout";
import { getAssetLocation, IVssLocationService, routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { Button, ExpandableButton } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ColumnDragDropBehavior } from "VSSUI/Components/Table/ColumnDragDropBehavior";
import { Icon, IconSize } from "VSSUI/Icon";
import { IIdentity } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { BoltListDragEvent, cellFromEvent, IListDropData, ListDragDropBehavior, ListDragImage } from "VSSUI/List";
import { ContextMenuBehavior, ContextualMenu, IMenuItem, MoreButton } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import {
    ColumnFill,
    ColumnMore,
    ITableColumn,
    renderEmptyCell,
    renderGripper,
    renderLoadingCell,
    SimpleTableCell,
    TableHeaderCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRowDetails, renderTreeRow, Tree, TreeRow, TreeRowRenderer } from "VSSUI/TreeEx";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { Location } from "VSSUI/Utilities/Position";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { WorkItemRollupProgressBar } from "Wit/AgileAnalytics/Components/WorkItemRollupProgressBar";
import { IAutomationRulesService } from "Wit/Common/AutomationRulesService/AutomationRulesService";
import { IBacklogShortcutService } from "Wit/Common/BacklogContent/IBacklogShortcutService";
import * as Resources from "Wit/Common/BacklogContent/Resources";
import { BacklogQueryResultType, IBacklogColumn, ITeam } from "Wit/Common/BacklogContentService/BacklogContentDataProviderTypes";
import {
    DisplayState,
    HubType,
    IBacklogTree,
    IBacklogTreeColumn,
    IBacklogTreeRow,
    IObservableForecastSettings
} from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemConfigurationService } from "Wit/Common/BacklogContentService/BacklogWorkItemConfigurationService";
import { IBacklogsHubContentService } from "Wit/Common/BacklogContentService/IBacklogsHubContentService";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { IHubContentService } from "Wit/Common/BacklogContentService/IHubContentService";
import { ISprintsHubContentService } from "Wit/Common/BacklogContentService/ISprintsHubContentService";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { performBulkUpdate } from "Wit/Common/BulkUpdateUtils/BulkUpdateUtils";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreFieldRefNames, FieldType, OobFieldRefNames, WorkItemStateCategories } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { ExceptionInfo } from "Wit/Common/HubError/Models/Models";
import { IPlanningPaneViewService } from "Wit/Common/PlanningPane/Services/PlanningPaneViewService";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { contains } from "Wit/Common/Utilities/Array";
import { DefaultNumberFormatOptions } from "Wit/Common/Utilities/FormatUtils";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { bulkUpdateWorkItems, getUnassignedMenuItem } from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { FetchWorkItemsDialog } from "Wit/Common/Utils/WorkItemUtils/FetchWorkItemsDialog";
import { isMeMacro } from "Wit/Common/WiqlOperators";
import { NewItemCallout } from "Wit/Common/WorkItemControls/NewItemCallout";
import { BulkEditDialog } from "Wit/Common/WorkItemDialogs/Components/BulkEditDialog";
import { ChangeParentPanel } from "Wit/Common/WorkItemDialogs/Components/ChangeParentPanel";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { EditTemplateDialog } from "Wit/Common/WorkItemDialogs/Components/EditTemplateDialog";
import { ProcessWorkItemsDialog, restoreSelection } from "Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { getWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { getWorkItemFieldService as getWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { IWorkItemTemplateService } from "Wit/Common/WorkItemTemplateService/IWorkItemTemplateService";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { FilterManager } from "Wit/Filter/FilterManager";
import { convertIIdentityToWorkItemIdentityRef, convertWorkItemIdentityRefFromFieldValue, getDistinctDisplayName } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { WorkItemTrackingDataProvider, WorkItemTypeIconProvider } from "Wit/UI/Data";
import { TagsList } from "Wit/UI/TagsList";
import { WorkItemState, WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { WorkItemTypeColorAndIcon, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { ILinkInfo, INamedFieldDataDictionary, IWorkItemTypeData, LinkCommand } from "Wit/WorkItemDataManager/Contracts";
import { convertHTMLCommentToMarkdown } from "Wit/WorkItemFormMarkdown/MarkdownUtils";
import { LinkTypeName } from "Wit/WorkItemModel/OM/WorkItemConstants";
import { IWorkItem, IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventData, IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import { htmlDecode } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { IWorkItemSelection } from "./Contracts/Contracts";
import { FilterHeader } from "./FilterHeader";
import { ForecastingVelocityHeader } from "./ForecastingVelocityHeader";
import { MoveToPositionDialog } from "./MoveToPositionDialog";

export interface BacklogItemsViewProps {
    onItemSelected?: (selectedItems: IWorkItemSelection[]) => void;
    newWorkItemCalloutAnchor: IObservableValue<HTMLElement | undefined>;
    pageContext: IVssPageContext;
    directoryDropdownProvider: DirectoryDropdownProvider;
    filterManager: FilterManager;
    hub: HubType;
    toggleFilter: () => void;
    toggleParents?: () => void;
    keepHierarchyWithFilters?: IObservableValue<boolean>;
    onMarkTTI: () => void;
    backlogWorkItemService?: IBacklogWorkItemService;
}

interface IAdvanceBacklogManagementPermission {
    hasPermission: boolean;
}

const menuRefs: ObservableArray<React.RefObject<ExpandableButton>> = new ObservableArray([]);
const iterationItems = new ObservableArray<IMenuItem>();
const itemsToUpdate = new Set<number>();

/** Must be kept in sync with the constant in BacklogsContentService.ts */
const c_unparentedItemIndex = -1;
const c_parentLinkType = -2;
const c_childLinkType = 2;
const c_contextMenuItemIndex = 4;

const backlogPermissionDataProvider = "ms.vss-work-web.backlog-permission-data-provider";
const workItemPermissionsDataProvider = "ms.vss-work-web.work-item-permissions-data-provider";

export function BacklogContentView(props: BacklogItemsViewProps): JSX.Element {
    const { pageContext, toggleFilter, toggleParents } = props;
    const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const backlogWorkItemService = props.backlogWorkItemService || pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
    const workItemEventService = pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
    const backlogWorkItemConfigurationService = pageContext.getService<IBacklogWorkItemConfigurationService>("IBacklogWorkItemConfigurationService");
    const workItemService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
    const hotKeyEventService = pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
    const planningPaneSvc = pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
    const automationRulesService = pageContext.getService<IAutomationRulesService>("IAutomationRulesService");
    const teamSettingsService = pageContext.getService<ITeamSettingsService>("ITeamSettingsService");

    const itemsBlockedForReordering = React.useRef(new Set<number>()).current;
    const workItemTypeStatesAndCategories = React.useRef<{ [workItemType: string]: WorkItemStateAndColor[] }>();
    const teamSettingsObservable = React.useRef(teamSettingsService.getTeamSettingsObservable()).current;
    const isLoading = React.useRef(true);
    const canBeDragOverable = React.useRef<boolean | undefined>(undefined);

    const [hasPermissionToDeleteWorkItem, setDeletePermission] = React.useState(true);
    const useNotLoadedWorkItemsFixEnabled = React.useMemo(() => {
        return !isFeatureFlagEnabled(
            pageContext,
            FeatureFlags.UseNotLoadedWorkItemsFixDisabled,
            false);
    }, []);
    const useBulkOperationsOptimization = React.useMemo(() => {
        return isFeatureFlagEnabled(
            pageContext,
            FeatureFlags.UseBulkOperationsOptimization,
            false);
    }, []);

    const showLoadingSpinnerOnFilterChange = React.useMemo(() => {
        return isFeatureFlagEnabled(pageContext, FeatureFlags.LoadingSpinnerOnFilterChangeEnabled, false);
    }, []);

    const useChangeParentPerfomanceOptimization = React.useMemo(() => {
        return isFeatureFlagEnabled(pageContext, FeatureFlags.UseChangeParentPerfomanceOptimization, false);
    }, []);

    async function setWorkItemTypeStatesAndCategories() {
        workItemTypeStatesAndCategories.current = await backlogWorkItemConfigurationService.getWorkItemStateAndColorDictionary();
    }

    const getOrFetchSelectedItemsCall = () => {
        return useNotLoadedWorkItemsFixEnabled
            ? getOrFetchSelectedItems(backlogContentService, backlogWorkItemService)
            : getSelectedItems(backlogContentService);
    }

    const deleteHotkeyCallback = async () => {
        if (document.querySelector(".work-item-form-page")) {
            return;
        }
        const items = await getOrFetchSelectedItemsCall();

        openDeleteDialog(backlogContentService, backlogWorkItemService, planningPaneSvc, items);
    };

    const copyItemCallback = () => {
        if (document.querySelector(".work-item-form-page")) {
            return;
        }

        WorkItemUtils.onCopyHTML(
            pageContext,
            getOrFetchSelectedItemsCall,
            backlogContentService.getBacklogTree().value?.visibleColumns ?? []);
    };

    React.useEffect(() => {
        hotKeyEventService.subscribe(deleteHotkeyCallback, IHotKeyEventType.WorkItemDelete);
        hotKeyEventService.subscribe(copyItemCallback, IHotKeyEventType.WorkItemCopy);
        return () => {
            hotKeyEventService.unsubscribe(deleteHotkeyCallback, IHotKeyEventType.WorkItemDelete);
            hotKeyEventService.unsubscribe(copyItemCallback, IHotKeyEventType.WorkItemCopy);
        };
    }, []);

    React.useEffect(() => {
        const getPermissions = async () => {
            const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
            const workItemPermissions = await contributionService.getDataAsync<IWorkItemPermissionData>(workItemPermissionsDataProvider);
            const hasPermissions = workItemPermissions?.workItemDeletePermission?.hasPermission;
            if (!hasPermissions) {
                setDeletePermission(false);
            }
        };
        getPermissions();
    }, [pageContext, setDeletePermission]);

    let agileBacklogPermission = false;
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const permissionData = contributionService.getData<IAdvanceBacklogManagementPermission>(backlogPermissionDataProvider);

    agileBacklogPermission = permissionData?.hasPermission ?? false;

    const hubService: IHubContentService = getHubService(pageContext, props.hub);

    function onContextMenu(event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) {
        const rowIndex = cellFromEvent(event).rowIndex;
        const merge = backlogContentService.getTreeSelection().selected(rowIndex) || !!event.shiftKey;
        backlogContentService.getTreeSelection().select(rowIndex, 1, merge);
        const mouseEvent = Object.keys(event).includes("pageX") && Object.keys(event).includes("pageY");

        const backlogTree = backlogContentService.getBacklogTree().value;
        const treeProviderItem = backlogTree?.itemProvider.value[rowIndex];

        // For mouse events, show menu where the click happened
        if (mouseEvent && treeProviderItem && treeProviderItem.underlyingItem.data.id > c_unparentedItemIndex) {
            const layoutService = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutService.renderCallout(onDismiss => {
                return (
                    <ContextualMenu
                        anchorOrigin={{ horizontal: Location.start, vertical: Location.start }}
                        anchorPoint={mouseEvent ? { x: (event as React.MouseEvent).pageX, y: (event as React.MouseEvent).pageY } : undefined}
                        menuProps={{
                            id: "backlog-menu",
                            items: getContextMenuItems(
                                backlogContentService,
                                backlogContentService.getBacklogTree().value!.itemProvider.value[rowIndex],
                                props.directoryDropdownProvider,
                                props.hub,
                                agileBacklogPermission,
                                hasPermissionToDeleteWorkItem
                            )
                        }}
                        menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
                        onDismiss={onDismiss}
                    />
                );
            });
        } else if (menuRefs.value[rowIndex]?.current) {
            menuRefs.value[rowIndex].current!.focus();
            window.requestAnimationFrame(() => menuRefs.value[rowIndex].current!.expand());
        }
        event.preventDefault();
    }

    const updateIterations = async (index: number, iteration: IIterationData) => {
        const useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
            pageContext,
            FeatureFlags.UseChangeIterationPerfomanceOptimization,
            false
        );

        if (useChangeIterationPerfomanceOptimization) {
            await getOrFetchSelectedItems(backlogContentService, backlogWorkItemService, undefined, true, useChangeIterationPerfomanceOptimization);
        }

        const planningPaneSvc = pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
        if (!planningPaneSvc.planningItemProvider) {
            const teamSettings = teamSettingsObservable.value;
            if (teamSettings) {
                const items = [];
                if (teamSettings.teamName) {
                    items.push(teamSettings.teamName);
                }
                if (teamSettings.currentIteration) {
                    items.push(teamSettings.currentIteration);
                }
                if (teamSettings.futureIterations && teamSettings?.futureIterations.length > 0) {
                    items.push(...teamSettings.futureIterations);
                }

                planningPaneSvc.planningItemProvider = new ArrayItemProvider(items);
            }
        }

        const selectedTreeItems = useChangeIterationPerfomanceOptimization 
            ? await backlogContentService.getSelectedProviderItemsWithChildren() 
            : backlogContentService.getSelectedProviderItems();

        const currentIterationData = planningPaneSvc.getIterationInfo(index);

        await backlogWorkItemService.updateIteration(
            currentIterationData,
            undefined,
            async (workItemDTO, numberOfSavedWorkitems) => {
                await hubService.onIterationChange(selectedTreeItems);
                planningPaneSvc.updateIterationPanel(index, workItemDTO, numberOfSavedWorkitems);
            },
            useChangeIterationPerfomanceOptimization,
            props.hub === HubType.Sprints);
    };

    const onItemSelected = () => {
        if (props.onItemSelected) {
            const selectedItems = getSelectedItems(backlogContentService).filter(item => item.value.id > 0);
            const eventArgs: IWorkItemSelection[] = selectedItems.map(item => ({
                workItemId: item.value.id,
                workItemType: item.value.value.workItemTypeColorAndIcon!.workItemTypeName
            }));
            props.onItemSelected(eventArgs);
        }
    };

    const getNonReorderableItemsFromSelected = (selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[]) =>
        selectedProviderItems.filter(
            item =>
                !backlogWorkItemService.isBacklogWorkItemReorderableAtAreaPath(
                    item.underlyingItem.data.value.coreColumnMap[backlogContentService.getTeamField() ?? CoreFieldRefNames.AreaPath] as string
                )
        );

    const contextMenuBehavior = new ContextMenuBehavior(onContextMenu);
    const backlogReorderingDragnDropHierarchyFixEnabled = !isFeatureFlagEnabled(pageContext, FeatureFlags.BacklogReorderingDragnDropHierarchyFixDisabled, false);
    const useDepthCalulationMethodInsideDragOverableCheck = !isFeatureFlagEnabled(pageContext, FeatureFlags.UseDepthCalculationMethodInsideDragOverableCheckDisabled, false);

    const listDragDropBehavior = React.useMemo(() => {
        return !agileBacklogPermission
            ? undefined
            : new ListDragDropBehavior({
                allowedTypes: ["backlog-item"],
                id: "backlog-tree",
                onDragStart: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>) => {
                    // the "Unparented Item(s)" row should not be draggable

                    // If selectedProviderItems does not contain currently held item, deselect other items and only select new item
                    if (backlogContentService.getSelectedProviderItems().indexOf(event.detail.dataTransfer.data) === -1) {
                        const treeSelection = backlogContentService.getTreeSelection();
                        const treeProvider: ITreeItemEx<IBacklogTreeRow>[] | undefined =
                            backlogContentService.getBacklogTree().value?.itemProvider.value;
                        treeSelection.clear();
                        const index = treeProvider?.findIndex(item => {
                            return item.underlyingItem.data.id === event.detail.dataTransfer.data.underlyingItem.data.id;
                        });
                        if (index !== undefined && index !== -1) {
                            treeSelection.select(index);
                        }
                    }

                    const selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[] = backlogContentService.getSelectedProviderItems(
                        event.detail.dataTransfer.data
                    );

                    if (!isDraggable(selectedProviderItems, hubService, workItemTypeStatesAndCategories.current)) {
                        event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                    } else {
                        event.detail.dataTransfer.effectAllowed = DragDropEffect.move;

                        // If multiple levels, make sure that each lower level is a child of one of the lowest depth items. If not, don't allow drop.
                        canBeDragOverable.current = everyChildHasParent(selectedProviderItems);
                    }
                },
                onDragOver: (event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>, dropData: IListDropData) => {
                    if (
                        canBeDragOverable.current === false ||
                        !isDragOverable(
                            event,
                            dropData,
                            backlogContentService,
                            backlogWorkItemService,
                            props.filterManager.isFiltering(),
                            props.keepHierarchyWithFilters,
                            itemsBlockedForReordering,
                            backlogReorderingDragnDropHierarchyFixEnabled,
                            useDepthCalulationMethodInsideDragOverableCheck
                        )
                    ) {
                        event.detail.dataTransfer.dropEffect = DragDropEffect.none;
                        return;
                    }

                    const itemProvider = backlogContentService.getBacklogTree().value?.itemProvider.value;

                    if (!itemProvider) {
                        return;
                    }

                    const droppedIndex = dropData.index - 1;
                    const selectedProviderItems = backlogContentService.getSelectedProviderItems(event.detail.dataTransfer.data);

                    const nonReorderableItems = getNonReorderableItemsFromSelected(selectedProviderItems);
                    if (nonReorderableItems.length) {
                        if (
                            nonReorderableItems.some(
                                item => item.parentItem?.underlyingItem.data.id !== itemProvider[droppedIndex]?.underlyingItem.data.id
                            )
                        ) {
                            const childItems = itemProvider[droppedIndex]?.underlyingItem.childItems;
                            if (childItems && childItems.length) {
                                const lastChild = childItems?.[childItems.length - 1];
                                const indexToRenderIndicatorLineAfter = itemProvider.findIndex(i => i.underlyingItem.data.id === lastChild.data.id);
                                return indexToRenderIndicatorLineAfter;
                            }
                        } else {
                            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
                            return;
                        }
                    }
                },
                onDrop: async (event, dropData) => {
                    let droppedIndex = dropData.index - 1;
                    const itemProvider: ITreeItemEx<IBacklogTreeRow>[] | undefined =
                        backlogContentService.getBacklogTree().value?.itemProvider.value;
                    canBeDragOverable.current = undefined;

                    if (itemProvider) {
                        // Determine prevItemId and nextItemId if reordering same level items
                        let selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[] = backlogContentService.getSelectedProviderItems(
                            event.detail.dataTransfer.data
                        );

                        const nonReorderableItems = getNonReorderableItemsFromSelected(selectedProviderItems);
                        if (
                            nonReorderableItems.length &&
                            nonReorderableItems.some(
                                item => item.parentItem?.underlyingItem.data.id !== itemProvider[droppedIndex]?.underlyingItem.data.id
                            )
                        ) {
                            const childItems = itemProvider[droppedIndex]?.underlyingItem.childItems;
                            if (childItems && childItems.length) {
                                const lastChild = childItems?.[childItems.length - 1];
                                const insertAfterItemIndex = itemProvider.findIndex(i => i.underlyingItem.data.id === lastChild.data.id);

                                droppedIndex = insertAfterItemIndex;
                            }
                        }

                        const lowestDepthProviderItems: ITreeItemEx<IBacklogTreeRow>[] = filterLowestDepthItems(selectedProviderItems);
                        let depthToUse = lowestDepthProviderItems[0].depth;

                        if (backlogReorderingDragnDropHierarchyFixEnabled) {
                            const uniqueWorkItemTypes = getUniqueWorkItemTypesForSelectedItems(selectedProviderItems);
                            const calculatedDepth = backlogContentService.getWorkitemTypesMinDepth(uniqueWorkItemTypes);
                            const greatestDepth = calculatedDepth === undefined || calculatedDepth < 0 ?
                                lowestDepthProviderItems[0].depth
                                : calculatedDepth;

                            depthToUse = greatestDepth;
                        }

                        // If reparenting
                        let nextItemId: number | null =
                            itemProvider[droppedIndex + 1]?.depth === depthToUse
                                ? itemProvider[droppedIndex + 1]?.underlyingItem.data.id
                                : null;

                        const firstSelectedWorkitemType = selectedProviderItems[0].underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
                        const depthToCheck = backlogReorderingDragnDropHierarchyFixEnabled && firstSelectedWorkitemType ?
                            backlogContentService.getWorkitemTypesMinDepth([firstSelectedWorkitemType])
                            : selectedProviderItems[0].depth;

                        if (backlogReorderingDragnDropHierarchyFixEnabled) {
                            //calculate and set depth for each element inside selectedProviderItems
                            selectedProviderItems.forEach(item => {
                                const workItemTypeName = item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
                                if (workItemTypeName) {
                                    const calculatedDepth = backlogContentService.getWorkitemTypesMinDepth([workItemTypeName]);
                                    item.depth = calculatedDepth;
                                }
                            });
                        }

                        while (itemProvider[droppedIndex]?.depth > depthToCheck) {
                            droppedIndex = itemProvider.findIndex(
                                i => i.underlyingItem.data.id === itemProvider[droppedIndex]?.parentItem?.underlyingItem.data.id
                            );
                        }

                        const droppedOnItemDepth = itemProvider[droppedIndex]?.depth;

                        let prevItemId: number | null = itemProvider[droppedIndex]?.underlyingItem.data.id ?? null;

                        if (
                            droppedOnItemDepth !== undefined &&
                            droppedOnItemDepth < depthToUse &&
                            !itemProvider[droppedIndex]?.underlyingItem.expanded
                        ) {
                            // dropped on collapsed parent so dont want to set nextItemId or prevItemId
                            nextItemId = null;
                            prevItemId = null;
                        } else if (
                            // dropped on expanded parent so need to set prevItem to undefined
                            droppedOnItemDepth !== undefined &&
                            droppedOnItemDepth < depthToUse &&
                            itemProvider[droppedIndex]?.underlyingItem.expanded
                        ) {
                            prevItemId = null;
                        }

                        let workItemIds: number[] = selectedProviderItems.map(items => items.underlyingItem.data.id);

                        // If multiple depth items are reordered, ignore everything but the lowest level item grabbed
                        if (lowestDepthProviderItems.length < selectedProviderItems.length) {
                            selectedProviderItems = lowestDepthProviderItems;
                            workItemIds = lowestDepthProviderItems.map(lowestDepthProviderItem => lowestDepthProviderItem.underlyingItem.data.id);
                            backlogContentService.insertItems(selectedProviderItems, undefined, droppedIndex);
                        } else {
                            // If same depth items are reordered, run onDrop before the workItem.parentItem is reassigned
                            backlogContentService.insertItems(selectedProviderItems, undefined, droppedIndex);

                            const backlogItemDepth: number = depthToUse;

                            // If current item is a child, check the backlog for the nearest parent (where depth is 1 less than backlogItemDepth)
                            if (backlogItemDepth > 0) {
                                for (let i = droppedIndex; i >= 0; i--) {
                                    if (backlogReorderingDragnDropHierarchyFixEnabled) {
                                        const currentWorkItemType = itemProvider[i].underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
                                        let depthToUse = itemProvider[i]?.depth; //unparented folder does not have workItemType

                                        if (currentWorkItemType) {
                                            depthToUse = backlogContentService.getWorkitemTypesMinDepth([currentWorkItemType]);
                                        }

                                        if (depthToUse === backlogItemDepth - 1) {
                                            lowestDepthProviderItems[0].parentItem = itemProvider[i];
                                            break;
                                        }
                                    } else {
                                        if (itemProvider[i]?.depth === backlogItemDepth - 1) {
                                            lowestDepthProviderItems[0].parentItem = itemProvider[i];
                                            break;
                                        }
                                    }
                                }
                            }
                        }

                        const currentIteration =
                            props.hub === HubType.Sprints ? (hubService as ISprintsHubContentService).getCurrentIteration() : undefined;

                        try {
                            if (!itemsBlockedForReordering?.size) {
                                await backlogWorkItemService.reorderWorkItem(
                                    workItemIds,
                                    lowestDepthProviderItems[0].parentItem?.underlyingItem.data,
                                    prevItemId,
                                    nextItemId,
                                    currentIteration,
                                    undefined,
                                    useChangeParentPerfomanceOptimization
                                );
                            } else {
                                _addItemsToBlockedForFutureReorders(selectedProviderItems);
                            }
                            workItemIds.forEach(workItemId => itemsToUpdate.add(workItemId));
                        } catch (e) {
                            getBoardsMessagesService(pageContext).showMessage(getErrorMessage(e));

                            _addItemsToBlockedForFutureReorders(selectedProviderItems);
                        }
                    }
                },
                renderDragImage: event => {
                    const backlogItem: IBacklogTreeRow = event.detail.dataTransfer.data.underlyingItem.data;
                    const selectedItems: IBacklogTreeRow[] = backlogContentService.getSelectedItems(backlogItem);
                    return (
                        <>
                            {selectedItems.map(item => {
                                return (
                                    <ListDragImage
                                        key={item.id}
                                        text={item.value.coreColumnMap[CoreFieldRefNames.Title] as string}
                                        iconProps={
                                            item.value.workItemTypeColorAndIcon && {
                                                render: () => (
                                                    <WorkItemTypeIcon className="icon-margin" data={item.value.workItemTypeColorAndIcon!} />
                                                )
                                            }
                                        }
                                    />
                                );
                            })}
                        </>
                    );
                },
                type: "backlog-item"
            });
    }, []);

    const _addItemsToBlockedForFutureReorders = (selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[]) => {
        for (const item of selectedProviderItems) {
            itemsBlockedForReordering.add(item.underlyingItem.data.id);
        }
    };

    const workItemChangedCallback = React.useCallback(async ({ workItem, changedWorkItemLinks }: IWorkItemEventData) => {
        const skipUIUpdateAfterWorkItemWasSaved = useBulkOperationsOptimization && backlogContentService.isBulkBacklogOperationInProgress();

        if (!skipUIUpdateAfterWorkItemWasSaved) {
            if (changedWorkItemLinks && changedWorkItemLinks.findIndex(link => shouldUpdateFromLink(link, backlogContentService)) >= 0) {
                hubService.refreshTree({ keepOpen: true, filterManager: props.filterManager });
            } else {
                await backlogContentService.updateWorkItemRow(workItem.id, workItem);
                removeWorkItemsIfNecessary(pageContext, [workItem], props.hub);
                const forecastSettings = props.hub === HubType.Backlogs ? (hubService as IBacklogsHubContentService).getForecastSettings() : undefined;
                if (forecastSettings?.value?.effortData && workItem.getFieldByName(forecastSettings.value.effortData.effortFieldName)) {
                    updateForecastLines(hubService, forecastSettings.value);
                }
            }
            planningPaneSvc.planningItemProvider && planningPaneSvc.updateIterations();
        }

        if (isFeatureFlagEnabled(pageContext, FeatureFlags.TeamAutomationRulesEnabled, false)) {
            await automationRulesService.evaluateAutomationRules(workItem);
        }
    }, []);

    React.useEffect(() => {
        workItemEventService.subscribe(workItemChangedCallback, IWorkItemEventType.WorkItemSaved);
        return () => workItemEventService.unsubscribe(workItemChangedCallback, IWorkItemEventType.WorkItemSaved);
    }, [workItemEventService, workItemChangedCallback]);

    const displayState = backlogContentService.getDisplayState();

    React.useEffect(() => {
        hubService.refreshTree({ filterManager: props.filterManager });
        const workItemFormDialogService = pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
        workItemFormDialogService.subscribeToHistoryService(undefined);
        const backlogShortcutService = pageContext.getService<IBacklogShortcutService>("IBacklogShortcutService");
        const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        backlogShortcutService.registerShortcuts(
            expandShallowestLevel,
            collapseDeepestLevel,
            updateIterations,
            toggleFilter,
            selectAllItems,
            toggleParents,
            props.hub === HubType.Sprints ? () => moveToTop(backlogContentService, backlogWorkItemService, pageContext) : undefined,
            () => layoutManager.toggleFullScreenMode()
        );

        const displayStateChange = (value: DisplayState) => {
            if (value === DisplayState.HasData) {
                setWorkItemTypeStatesAndCategories();
                const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
                const backlogQueryResultType = contentService.getQueryType();
                const projectName = getTFSPageData(pageContext)?.project?.name;
                if (props.hub === HubType.Backlogs && backlogQueryResultType && projectName && teamSettingsObservable.value?.teamId) {
                    const projectUrl = routeUrl(pageContext, "ms.vss-tfs-web.project-overview-route", { project: projectName });
                    const teamSettingsUrl = `${projectUrl}/_settings/work-team?teamId=${teamSettingsObservable.value.teamId}&_a=areas`;
                    const link = (
                        <a href={teamSettingsUrl} target="_blank" rel="noopener noreferrer">
                            {Resources.ReduceAreaOfOwnershipLinkText}
                        </a>
                    );

                    const renderMessage = () => (
                        <FormatComponent
                            format={
                                backlogQueryResultType === BacklogQueryResultType.OneHop
                                    ? Resources.TooManyItemsOnBacklog_ShowingOneHop
                                    : Resources.TooManyItemsOnBacklog
                            }
                            children={link}
                        />
                    );

                    getBoardsMessagesService(pageContext).showMessage({
                        renderMessage: renderMessage,
                        severity: MessageCardSeverity.Info
                    });
                }
            }
        };

        displayState.subscribe(displayStateChange);

        // unmount
        return () => {
            backlogShortcutService.unregisterShortcuts();
            displayState.unsubscribe(displayStateChange);
        };
    }, []);

    const getForecastSettings = (): ObservableValue<IObservableForecastSettings | undefined> => {
        return props.hub === HubType.Backlogs ? (hubService as IBacklogsHubContentService).getForecastSettings() : new ObservableValue(undefined);
    };

    const useTeamSettings = () => {
        iterationItems.value = WorkItemUtils.setIterationItems(teamSettingsObservable.value, updateIterations).value;
    };

    React.useEffect(() => {
        useTeamSettings();
        teamSettingsObservable.subscribe(useTeamSettings);
        return () => teamSettingsObservable.unsubscribe(useTeamSettings);
    }, []);

    useSubscription(backlogContentService.getBacklogTree(), (_) => {
        if (isLoading.current) {
            props.onMarkTTI();
            isLoading.current = false;
        }
    });

    const loadingItems = new TreeItemProvider<IBacklogTreeRow>([]);
    const isNewSprintsViewEnabled = isFeatureFlagEnabled(pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    return (
        <div className={`backlogs-view flex-row flex-start flex-grow v-scroll-auto depth-8 ${isNewSprintsViewEnabled ? 'backlogs-view-sprints-ff-enabled' : ''}`}>
            <Card className="bolt-table-card flex-grow full-height">
                <Observer tree={backlogContentService.getBacklogTree()} displayState={displayState} forecastSettings={getForecastSettings()}>
                    {(observerProps: {
                        tree: IBacklogTree | undefined;
                        displayState: DisplayState;
                        forecastSettings: IObservableForecastSettings | undefined;
                    }) => {
                        const { tree, displayState } = observerProps;
                        const itemProvider = tree?.itemProvider;
                        const columns = initializeItemTable(
                            props.pageContext,
                            menuRefs,
                            props.directoryDropdownProvider,
                            props.hub,
                            agileBacklogPermission,
                            hasPermissionToDeleteWorkItem
                        );

                        const columnDragDropBehavior = new ColumnDragDropBehavior({
                            type: "column",
                            allowedTypes: ["column"],
                            columns: columns.treeColumns,
                            renderDragImage: event => {
                                const columnName = event.detail.dataTransfer.data;
                                return <ListDragImage text={columnName.name ?? ""} />;
                            },
                            onDragStart: event => {
                                if (event.detail.dataTransfer.secondaryData?.index === 0 || event.detail.dataTransfer.secondaryData?.index === 1) {
                                    event.detail.dataTransfer.dropEffect = DragDropEffect.none;
                                }
                            },
                            onDragOver: (event, dropData) => {
                                if (dropData.index === 0 || dropData.index === 1) {
                                    event.detail.dataTransfer.dropEffect = DragDropEffect.none;
                                }
                            },
                            onDrop: async (event, dropData) => {
                                const columns = await hubService.settingsServiceGetColumns();
                                const movedColumn = columns.find(col => col.name === event.detail.dataTransfer.data.id);
                                if (movedColumn) {
                                    // Add new - subtract extra 1 because "order" and "+/-" columns don't exist in this list
                                    const index = dropData.index > c_contextMenuItemIndex ? dropData.index - 3 : dropData.index - 2;
                                    columns.splice(index, 0, movedColumn);
                                    // Remove old
                                    const movedColCurrentIndex = columns.findIndex((col, idx) => col.name === movedColumn.name && idx !== index);
                                    columns.splice(movedColCurrentIndex, 1);

                                    try {
                                        // Must wait for first call to complete before refreshing tree to prevent race condition
                                        await hubService.settingsServiceSetColumns(columns);
                                        hubService.refreshTree({ filterManager: props.filterManager });
                                    } catch (e) {
                                        getBoardsMessagesService(pageContext).showMessage(getErrorMessage(e));
                                    }
                                }
                            }
                        });

                        if (observerProps.forecastSettings) {
                            updateForecastLines(hubService, observerProps.forecastSettings);
                        }
                        return (
                            <div className="flex-column flex-grow h-scroll-auto">
                                <Observer exceptionInfo={backlogContentService.getExceptionInfo()}>
                                    {(exceptionObserverProps: { exceptionInfo: ExceptionInfo }) => {
                                        return exceptionObserverProps.exceptionInfo ? (
                                            <HubError exceptionsInfo={[exceptionObserverProps.exceptionInfo]} />
                                        ) : (
                                            <>
                                                <Observer showFilterHeader={backlogContentService.getShowFilterHeader()}>
                                                    {(observerProps: { showFilterHeader: boolean }) => {
                                                        {
                                                            if (
                                                                observerProps.showFilterHeader &&
                                                                itemProvider &&
                                                                displayState === DisplayState.HasData
                                                            ) {
                                                                return (
                                                                    <FilterHeader
                                                                        pageContext={pageContext}
                                                                        onFilterMoreItems={toPageCount => {
                                                                            if (showLoadingSpinnerOnFilterChange) {
                                                                                backlogContentService.getIsLoadingNextBatchObservable().value = true;
                                                                            }
                                                                            backlogWorkItemService.pageMoreItems(toPageCount);
                                                                        }}
                                                                        unPagedItemCount={backlogContentService.getWorkItemIdsSet().size}
                                                                        totalItemCount={
                                                                            Object.keys(backlogContentService.getBacklogTree().value!.allRows).length
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                            return null;
                                                        }
                                                    }}
                                                </Observer>
                                                <Observer showQueryHeader={backlogContentService.getShowQueryHeader()}>
                                                    {(observerProps: { showQueryHeader: boolean }) => {
                                                        if (observerProps.showQueryHeader && itemProvider && displayState === DisplayState.HasData) {
                                                            return (
                                                                <MessageCard
                                                                    className="backlogs-query-header-color"
                                                                    onDismiss={() => (backlogContentService.getShowQueryHeader().value = false)}
                                                                >
                                                                    <div className="flex-column justify-center flex-self-center margin-right-4">
                                                                        <span className="font-size-m">{Resources.CreatedQuery}</span>
                                                                    </div>
                                                                    <Link
                                                                        onClick={() =>
                                                                            onClickFPS(pageContext, backlogContentService.getRecentQueryUrl(), true)
                                                                        }
                                                                    >
                                                                        {Resources.ClickToView}
                                                                    </Link>
                                                                </MessageCard>
                                                            );
                                                        } else {
                                                            return null;
                                                        }
                                                    }}
                                                </Observer>
                                                {itemProvider &&
                                                    itemProvider.length > 0 &&
                                                    displayState === DisplayState.HasData &&
                                                    observerProps.forecastSettings?.visibleState === "on" && (
                                                        <ForecastingVelocityHeader
                                                            velocity={observerProps.forecastSettings.velocity}
                                                            onVelocityUpdated={
                                                                (hubService as IBacklogsHubContentService).updateForecastVelocityAndLines
                                                            }
                                                        />
                                                    )}
                                                {itemProvider && itemProvider.length > 0 && displayState === DisplayState.HasData && (
                                                    <Tree<IBacklogTreeRow>
                                                        onSelect={onItemSelected}
                                                        behaviors={
                                                            listDragDropBehavior
                                                                ? [contextMenuBehavior, listDragDropBehavior, columnDragDropBehavior]
                                                                : [contextMenuBehavior, columnDragDropBehavior]
                                                        }
                                                        className="backlog-tree sticky-table"
                                                        containerClassName="sticky-table full-height"
                                                        columns={columns.treeColumns}
                                                        itemProvider={itemProvider || loadingItems}
                                                        onToggle={(
                                                            event: React.SyntheticEvent<HTMLElement>,
                                                            treeItem: ITreeItemEx<IBacklogTreeRow>
                                                        ) => {
                                                            itemProvider?.toggle(treeItem.underlyingItem);

                                                            const children = treeItem.underlyingItem.data.children;
                                                            if (treeItem.underlyingItem.expanded) {
                                                                // on expand, fetch any unloaded children
                                                                children
                                                                    .filter((child: IBacklogTreeRow) => {
                                                                        return child.value.columnValues.length === 0;
                                                                    })
                                                                    .forEach((child: IBacklogTreeRow) => {
                                                                        backlogWorkItemService.fetchWorkItem(child.id);
                                                                    });
                                                            }
                                                            // When expanded, we need to move down the forecast lines to below the last children
                                                            // the lines should move back to the parent when collapsed
                                                            if (treeItem.underlyingItem.data.iteration && treeItem.underlyingItem.expanded) {
                                                                children[children.length - 1].iteration = treeItem.underlyingItem.data.iteration;
                                                                treeItem.underlyingItem.data.iteration = undefined;
                                                            } else if (!treeItem.underlyingItem.expanded && children[children.length - 1].iteration) {
                                                                treeItem.underlyingItem.data.iteration = children[children.length - 1].iteration;
                                                                children[children.length - 1].iteration = undefined;
                                                            }
                                                        }}
                                                        renderSpacer={(index: number, left: boolean) => {
                                                            const item = itemProvider?.value[index];

                                                            if (item && isDraggable([item], hubService, workItemTypeStatesAndCategories.current)) {
                                                                return renderGripper(index, left);
                                                            }
                                                        }}
                                                        scrollable={true}
                                                        selection={backlogContentService.getTreeSelection()}
                                                        showLines={false}
                                                        showScroll={true}
                                                        singleClickActivation={false}
                                                        renderRow={(index, item, details) =>
                                                            renderRow(pageContext, index, item, details, columns.treeColumns)
                                                        }
                                                    />
                                                )}
                                                {itemProvider && !itemProvider.length && displayState === DisplayState.HasData && (
                                                    <ZeroData
                                                        className="padding-16"
                                                        imagePath={getAssetLocation(
                                                            pageContext,
                                                            "ms.vss-work-web/common-backlog-content/Content/zerodata-no-results-found.svg"
                                                        )}
                                                        imageAltText={Resources.ZeroDataNoResultsFound}
                                                        secondaryText={Resources.ZeroDataNoResultsFound}
                                                    />
                                                )}
                                                {displayState === DisplayState.Loading && (
                                                    <div className="flex-row flex-start flex-grow flex-self-center">
                                                        <Spinner
                                                            className="flex-self-center flex-grow padding-16"
                                                            size={SpinnerSize.large}
                                                            label={Resources.Loading}
                                                        />
                                                    </div>
                                                )}
                                                {displayState === DisplayState.ZeroData && (
                                                    <div className="flex-column flex-start flex-grow flex-self-center">
                                                        <ZeroData
                                                            className="flex-self-center"
                                                            imagePath={getAssetLocation(
                                                                pageContext,
                                                                "ms.vss-work-web/common-backlog-content/Content/zerodata-no-work-scheduled.svg"
                                                            )}
                                                            imageAltText={hubService.getZeroDataPrimaryText()}
                                                            primaryText={hubService.getZeroDataPrimaryText()}
                                                            secondaryText={hubService.getZeroDataSecondaryText()}
                                                            actionText={Resources.NewWorkItem}
                                                            actionType={ZeroDataActionType.ctaButton}
                                                            onActionClick={ev => (props.newWorkItemCalloutAnchor.value = ev?.currentTarget)}
                                                        />
                                                        {props.hub === HubType.Backlogs && (
                                                            <Link
                                                                className="flex-self-center padding-16"
                                                                href="https://go.microsoft.com/fwlink/?linkid=867428"
                                                                target="_new"
                                                            >
                                                                {Resources.LearnMoreAboutBacklogs}
                                                            </Link>
                                                        )}
                                                    </div>
                                                )}
                                                <Observer
                                                    calloutAnchor={backlogContentService.getNewItemCalloutAnchor()}
                                                    workItemTypeColorAndIcons={backlogWorkItemConfigurationService.getObservableWorkItemTypeColorAndIcons()}
                                                >
                                                    {(observerProps: {
                                                        calloutAnchor: HTMLElement | undefined;
                                                        workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[];
                                                    }) => {
                                                        if (observerProps.calloutAnchor) {
                                                            const sortedBacklogWorkItemTypeNames =
                                                                backlogContentService.getBacklogWorkItemTypeNames();
                                                            const workItemTypeColorAndIcon: WorkItemTypeColorAndIcon[] =
                                                                observerProps.workItemTypeColorAndIcons.filter(
                                                                    (colorAndIcon: WorkItemTypeColorAndIcon) => {
                                                                        return sortedBacklogWorkItemTypeNames.some(
                                                                            backlogWorkItemTypeName =>
                                                                                backlogWorkItemTypeName === colorAndIcon.workItemTypeName
                                                                        );
                                                                    }
                                                                );
                                                            const projectData = getTFSProject(pageContext);
                                                            workItemTypeColorAndIcon.sort(
                                                                (a, b) =>
                                                                    sortedBacklogWorkItemTypeNames.indexOf(a.workItemTypeName) -
                                                                    sortedBacklogWorkItemTypeNames.indexOf(b.workItemTypeName)
                                                            );
                                                            return (
                                                                <NewItemCallout
                                                                    anchor={observerProps.calloutAnchor}
                                                                    colorAndIcons={workItemTypeColorAndIcon}
                                                                    keepOpenAfterAddingItem={true}
                                                                    onAddItem={async (title, witType, direction) => {
                                                                        const iteration =
                                                                            props.hub === HubType.Sprints
                                                                                ? (hubService as ISprintsHubContentService).getCurrentIteration()
                                                                                : undefined;
                                                                        if (!title) {
                                                                            if (!projectData?.id) {
                                                                                return;
                                                                            }

                                                                            const types: IWorkItemTypeData[] | null =
                                                                                await workItemService.getWorkItemTypeData(projectData?.id, [witType]);
                                                                            if (!types || !types.length) {
                                                                                return undefined;
                                                                            }

                                                                            openDialog(backlogContentService, onDismiss => {
                                                                                return (
                                                                                    <WorkItemFormDialog
                                                                                        onDismiss={onDismiss}
                                                                                        workItemTypeName={witType}
                                                                                        onSaveComplete={workItem =>
                                                                                            backlogWorkItemService.addCreatedWorkItem(
                                                                                                workItem,
                                                                                                undefined,
                                                                                                direction
                                                                                            )
                                                                                        }
                                                                                        initialValues={{
                                                                                            [CoreFieldRefNames.IterationId]: iteration?.id
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            });
                                                                        } else {
                                                                            backlogWorkItemService.queueOperation(() =>
                                                                                backlogWorkItemService.addWorkItem(
                                                                                    title,
                                                                                    witType,
                                                                                    direction,
                                                                                    iteration?.id
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                    onDismiss={() =>
                                                                        (backlogContentService.getNewItemCalloutAnchor().value = undefined)
                                                                    }
                                                                    getBacklogDefaultWorkItemTypeNameFetcher={{ service: backlogContentService }}
                                                                />
                                                            );
                                                        }

                                                        return null;
                                                    }}
                                                </Observer>
                                            </>
                                        );
                                    }}
                                </Observer>
                            </div>
                        );
                    }}
                </Observer>
            </Card>
        </div>
    );
}

function getUniqueWorkItemTypesForSelectedItems(selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[]) {
    const uniqueWorkItemTypes = Array.from(
        new Set(
            selectedProviderItems
                .map(item => item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString())
                .filter((typeName): typeName is string => typeName !== undefined)
        )
    );
    return uniqueWorkItemTypes;
}

function filterLowestDepthItems(items: ITreeItemEx<IBacklogTreeRow>[]) {
    return items.reduce((lowestDepthItems: ITreeItemEx<IBacklogTreeRow>[], item: ITreeItemEx<IBacklogTreeRow>) => {
        if (lowestDepthItems.length === 0 || lowestDepthItems[0].depth > item.depth) {
            lowestDepthItems = [item];
        } else if (lowestDepthItems[0].depth === item.depth) {
            lowestDepthItems.push(item);
        }
        return lowestDepthItems;
    }, []);
}

function initializeItemTable(
    pageContext: IVssPageContext,
    menuRefs: ObservableArray<React.RefObject<ExpandableButton>>,
    directoryDropdownProvider: DirectoryDropdownProvider,
    hub: HubType,
    agileBacklogPermission: boolean,
    hasPermissionToDeleteWorkItem: boolean
): IColumnDetails {
    const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const backlogTree = contentService.getBacklogTree().value;

    const columnDetails: IColumnDetails = {
        treeColumns: [],
        titleColumnIndex: -1
    };

    if (backlogTree && backlogTree.itemProvider) {
        if (!menuRefs.length) {
            backlogTree.rows.forEach(row => menuRefs.push(React.createRef<ExpandableButton>()));
        }
        columnDetails.titleColumnIndex = backlogTree.visibleColumns.findIndex(c => c.name === CoreFieldRefNames.Title);

        const hubService: IHubContentService = getHubService(pageContext, hub);
        const forecastSettings = hub === HubType.Backlogs ? (hubService as IBacklogsHubContentService).getForecastSettings().value : undefined;
        if (forecastSettings?.visibleState === "on") {
            columnDetails.treeColumns.push({
                id: "forecast-column",
                name: Resources.Forecast,
                renderCell: renderForecastCell,
                width: 150
            });
        }
        const wrappedRenderAddCell = (
            rowIndex: number,
            columnIndex: number,
            treeColumn: IBacklogTreeColumn,
            treeItem: ITreeItemEx<IBacklogTreeRow>
        ) => renderAddCell(pageContext, rowIndex, columnIndex, treeColumn, treeItem);

        const wrappedRenderHeaderCell = (columnIndex: number, treeColumn: IBacklogTreeColumn) =>
            renderHeaderCell(pageContext, columnIndex, treeColumn);

        // show expand/collapse column first
        columnDetails.treeColumns.push({
            id: "expand-collapse-column",
            renderHeaderCell: wrappedRenderHeaderCell,
            renderCell: wrappedRenderAddCell,
            width: 70
        });

        const wrappedRenderOrderCell = (
            rowIndex: number,
            columnIndex: number,
            treeColumn: IBacklogTreeColumn,
            treeItem: ITreeItemEx<IBacklogTreeRow>
        ) => renderOrderCell(contentService, rowIndex, columnIndex, treeColumn, treeItem);

        const wrappedRenderRollupCell = (
            rowIndex: number,
            columnIndex: number,
            treeColumn: IBacklogTreeColumn,
            treeItem: ITreeItemEx<IBacklogTreeRow>
        ) => renderRollupCell(contentService, rowIndex, columnIndex, treeColumn, treeItem);

        const showOrderIcon = !contentService.getShouldShowOrderValues();
        // show the order column before the backlog columns
        columnDetails.treeColumns.push({
            id: "order-col",
            renderHeaderCell: renderOrderHeaderCell,
            iconProps: showOrderIcon
                ? {
                    className: "backlogs-header-warning-icon",
                    iconName: "Info",
                    tooltipProps: { text: Resources.OrderCannotBeShown }
                }
                : undefined,
            name: Resources.Order,
            renderCell: wrappedRenderOrderCell,
            width: showOrderIcon ? 72 : 60
        });

        const wrappedRenderTitleCell = (
            rowIndex: number,
            columnIndex: number,
            treeColumn: IBacklogTreeColumn,
            treeItem: ITreeItemEx<IBacklogTreeRow>
        ) => renderTitleCell(pageContext, rowIndex, columnIndex, treeColumn, treeItem);

        const wrappedRenderParentCell = (
            rowIndex: number,
            columnIndex: number,
            treeColumn: IBacklogTreeColumn,
            treeItem: ITreeItemEx<IBacklogTreeRow>
        ) => renderParentCell(pageContext, rowIndex, columnIndex, treeColumn, treeItem);

        const wrappedRenderRollupHeaderCell = (columnIndex: number, treeColumn: IBacklogTreeColumn) =>
            renderRollupHeaderCell(pageContext, columnIndex, treeColumn);

        // convert the backlog columns into tree columns
        columnDetails.treeColumns.push(
            ...backlogTree.rollupColumns.map(column => {
                if (column.rollup && column.rollupCalculation) {
                    return {
                        columnDetails: column,
                        columnIndex: column.columnIndex,
                        dataColumn: column.columnIndex,
                        id: column.name,
                        hierarchical: false,
                        minWidth: 50,
                        name: contentService.getRollupColumnNameAlignedWithBacklog(column.rollupCalculation),
                        renderCell: wrappedRenderRollupCell,
                        renderHeaderCell: wrappedRenderRollupHeaderCell,
                        onSize: !column.fixed ? getOnSizeColumn(hubService, backlogTree.rollupColumns) : undefined,
                        width: !column.fixed ? new ObservableValue<number>(column.width) : column.width
                    };
                } else {
                    return {
                        columnDetails: column,
                        columnIndex: backlogTree.columnMap[column.name],
                        dataColumn: backlogTree.columnMap[column.name],
                        id: column.name,
                        hierarchical: column.name === CoreFieldRefNames.Title,
                        minWidth: 50,
                        name: column.text,
                        renderCell:
                            column.name === CoreFieldRefNames.Title
                                ? wrappedRenderTitleCell
                                : column.name === CoreFieldRefNames.State
                                    ? renderStateCell
                                    : column.name === CoreFieldRefNames.Tags
                                        ? renderTagsCell
                                        : column.name === CoreFieldRefNames.Parent
                                            ? wrappedRenderParentCell
                                            : column.name === OobFieldRefNames.RemainingWork
                                                ? renderRemainingWorkCell
                                                : renderCell,
                        headerClassName: column.name === CoreFieldRefNames.Title ? "backlog-tree-title-header-cell" : undefined,
                        onSize: !column.fixed ? getOnSizeColumn(hubService, backlogTree.rollupColumns) : undefined,
                        width: !column.fixed ? new ObservableValue<number>(column.width) : column.width
                    };
                }
            })
        );

        // add our overflow commands
        const contextMenuIndex =
            columnDetails.treeColumns.length > c_contextMenuItemIndex ? c_contextMenuItemIndex : columnDetails.treeColumns.length;
        columnDetails.treeColumns.splice(
            contextMenuIndex,
            0,
            new ColumnMore(
                item => {
                    return {
                        id: "backlog-menu",
                        items: getContextMenuItems(
                            contentService,
                            item,
                            directoryDropdownProvider,
                            hub,
                            agileBacklogPermission,
                            hasPermissionToDeleteWorkItem
                        )
                    };
                },
                undefined,
                (rowIndex: number, columnIndex: number, event: React.MouseEvent | React.KeyboardEvent) => {
                    const merge = contentService.getTreeSelection().selected(rowIndex) || !!event.shiftKey;
                    contentService.getTreeSelection().select(rowIndex, 1, merge);
                },
                undefined,
                menuRefs
            )
        );

        // we want the table to fill the container at a minimum
        columnDetails.treeColumns.push(ColumnFill);
    }

    return columnDetails;
}

async function renderMenuItem(
    pageContext: IVssPageContext,
    layoutManager: IVssLayoutManager,
    workItemType: string,
    contentService: ICommonBacklogContentService,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): Promise<void> {
    const projectId = getTFSPageData(pageContext)?.project?.id;
    if (!projectId) {
        return undefined;
    }
    const itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined = contentService.getBacklogTree().value?.itemProvider;
    const linksService = pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService");
    const workItemService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
    const backlogWorkItemService = pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");

    const types: IWorkItemTypeData[] | null = await workItemService.getWorkItemTypeData(projectId, [workItemType]);
    if (!types || !types.length) {
        return undefined;
    }

    const item = await workItemService.createWorkItem(types[0], projectId);

    // set values for iteration and area fields from parent wi
    if (treeItem.underlyingItem.data.value?.coreColumnMap) {
        const parentColumns = treeItem.underlyingItem.data.value.coreColumnMap;
        if (parentColumns[CoreFieldRefNames.IterationId]) {
            item.setFieldValueByName(CoreFieldRefNames.IterationId, parentColumns[CoreFieldRefNames.IterationId]);
        }
        if (parentColumns[CoreFieldRefNames.IterationPath]) {
            item.setFieldValueByName(CoreFieldRefNames.IterationPath, parentColumns[CoreFieldRefNames.IterationPath]);
        }
        if (parentColumns[CoreFieldRefNames.AreaId]) {
            item.setFieldValueByName(CoreFieldRefNames.AreaId, parentColumns[CoreFieldRefNames.AreaId]);
        }
        if (parentColumns[CoreFieldRefNames.AreaPath]) {
            item.setFieldValueByName(CoreFieldRefNames.AreaPath, parentColumns[CoreFieldRefNames.AreaPath]);
        }
    }

    item.addLinks([await WorkItemLink.create(pageContext, LinkTypeName.Parent_Link_Name, treeItem.underlyingItem.data.id)]);

    linksService.refreshLinkData(item);

    layoutManager.renderCallout(onDismiss => {
        return (
            <WorkItemFormDialog
                workItem={item}
                onDismiss={onDismiss}
                onSaveComplete={async () => {
                    if (!treeItem.underlyingItem?.expanded) {
                        itemProvider?.toggle(treeItem.underlyingItem);
                    }
                    backlogWorkItemService.addChildWorkItem(item, treeItem.underlyingItem);
                    const parentWorkItem = await workItemService.getWorkItem(treeItem.underlyingItem.data.id);
                    await parentWorkItem.refreshNewLinks();
                    linksService.refreshLinkData(parentWorkItem);
                }}
            />
        );
    });
}

function getOnSizeColumn(
    hubService: IHubContentService,
    rollupColumns: IBacklogColumn[]
): (event: MouseEvent, index: number, width: number, column: IBacklogTreeColumn) => void {
    return (event: MouseEvent, index: number, width: number, column: IBacklogTreeColumn) => {
        (column.width as IObservableValue<number>).value = width;

        if (event.type === "mouseup" && column.columnDetails.fieldId) {
            const rollupColumn = rollupColumns.find(rollupColumn => rollupColumn.fieldId === column.columnDetails.fieldId);
            if (rollupColumn) {
                rollupColumn.width = width;
            }
            hubService.settingsServiceResizeColumn(column.columnDetails.fieldId, width);
        }
    };
}

function openDialog(backlogContentService: ICommonBacklogContentService, callout: (onDismiss: () => void) => JSX.Element): void {
    const layoutManager = backlogContentService.getPageContext().getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => callout(onDismiss));
}

function openDeleteDialog(
    backlogContentService: ICommonBacklogContentService,
    backlogWorkItemService: IBacklogWorkItemService,
    planningPaneSvc: IPlanningPaneViewService,
    items: INamedFieldDataDictionary[]
): void {
    openDialog(backlogContentService, onDismiss => {

        return (
            <DeleteDialog
                onDismiss={onDismiss}
                onDeleteItems={items => {
                    backlogWorkItemService.deleteWorkItem(items.map(item => item.value));
                    planningPaneSvc.planningItemProvider && planningPaneSvc.updateIterations();
                }}
                onDeleteFinished={() => {
                    backlogContentService.resetOrderColumn();
                    backlogContentService.getTreeSelection().clear();
                }}
                selectedItems={items}
            />
        );
    });
}

async function getOrFetchSelectedItems(
    backlogContentService: ICommonBacklogContentService,
    backlogWorkItemService: IBacklogWorkItemService,
    currentItem?: ITreeItemEx<IBacklogTreeRow>,
    fetchChildren: boolean = false,
    useFetchChildrenOptimization: boolean = false,
): Promise<({ value: IBacklogTreeRow } & INamedFieldDataDictionary)[]> {
    const selection = backlogContentService.getTreeSelection();
    const pageContext = backlogContentService.getPageContext();
    const useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
        pageContext,
        FeatureFlags.UseChangeIterationPerfomanceOptimization,
        false
    );
    let notFetchedWorkItemIds: number[] = [];
    if (useChangeIterationPerfomanceOptimization) {
        notFetchedWorkItemIds = await backlogContentService.getSelectedItemsIdFromRawBacklogTree(fetchChildren, useFetchChildrenOptimization);
    } else {
        const itemProvider = backlogContentService.getBacklogTree().value?.itemProvider;
        for (const range of selection.value) {
            for (let i = range.beginIndex; i <= range.endIndex; i++) {
                const item = itemProvider?.value[i];
                const iBacklogTreeRow = item?.underlyingItem.data;
                if (iBacklogTreeRow && !iBacklogTreeRow.value.coreColumnMap[CoreFieldRefNames.Id]) {
                    const workItemId = iBacklogTreeRow.id;
                    notFetchedWorkItemIds.push(workItemId);
                }
            }
        }
    }
    if (notFetchedWorkItemIds.length > 0) {
        const selectionCopy = selection.value.map(item => ({ ...item }));
        return await new Promise(resolve => {
            openDialog(backlogContentService,
                onDismiss => (
                    <FetchWorkItemsDialog
                        onDismiss={() => {
                            onDismiss();
                            resolve(getSelectedItems(backlogContentService, currentItem));
                        }}
                        processWorkItems={async (workItemIds) => {
                            await backlogWorkItemService.fetchWorkItems(workItemIds);
                            return true;
                        }}
                        workItemIds={notFetchedWorkItemIds}
                        callBack={() => {
                            if (useChangeIterationPerfomanceOptimization) {
                                restoreSelection(selectionCopy, selection);
                            } else {
                                for (const range of selectionCopy) {
                                    selection.select(range.beginIndex, range.endIndex - range.beginIndex + 1);
                                }
                            }
                        }} />
                ));
        });
    } else {
        return getSelectedItems(backlogContentService, currentItem);
    }
}

function getSelectedItems(
    backlogContentService: ICommonBacklogContentService,
    currentItem?: ITreeItemEx<IBacklogTreeRow>
): ({ value: IBacklogTreeRow } & INamedFieldDataDictionary)[] {
    return backlogContentService.getSelectedItems(currentItem?.underlyingItem?.data).map(item => ({
        value: item,
        id: item.value.coreColumnMap[CoreFieldRefNames.Id],
        teamProject: item.value.coreColumnMap[CoreFieldRefNames.TeamProject] as string,
        ...item.value.coreColumnMap
    }));
}

function getContextMenuItems(
    backlogContentService: ICommonBacklogContentService,
    currentItem: ITreeItemEx<IBacklogTreeRow>,
    directoryDropdownProvider: DirectoryDropdownProvider,
    hub: HubType,
    agileBacklogPermission: boolean,
    hasPermissionToDeleteWorkItem: boolean
): ObservableCollection<IMenuItem> {
    const menuItems = new ObservableCollection<IMenuItem>();
    const pageContext = backlogContentService.getPageContext();

    const useNewWorkItemFieldService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false);
    const useChangeParentPerfomanceOptimization = isFeatureFlagEnabled(pageContext, FeatureFlags.UseChangeParentPerfomanceOptimization, false);

    const workItemFieldService = !useNewWorkItemFieldService ? getWorkItemFieldService(pageContext) : undefined;
    const workItemFieldService2 = useNewWorkItemFieldService ? getWorkItemFieldService2(pageContext) : undefined;
    const backlogWorkItemService = pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
    const planningPaneSvc = pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
    const workItemFormSavingService = pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
    const useNotLoadedWorkItemsFixEnabled = !isFeatureFlagEnabled(
        pageContext,
        FeatureFlags.UseNotLoadedWorkItemsFixDisabled,
        false
    );
    const useBulkOperationsOptimization: boolean = isFeatureFlagEnabled(pageContext, FeatureFlags.UseBulkOperationsOptimization, false);
    const useProcessingDialogForLongBacklogOperations = isFeatureFlagEnabled(pageContext, FeatureFlags.UseProcessingDialogForLongBacklogOperationsEnabled, false);

    const hubService: IHubContentService = getHubService(pageContext, hub);

    const getSelectedItemsCall = () => {
        return getSelectedItems(backlogContentService, currentItem);
    };

    const getOrFetchSelectedItemsCall = () => {
        return useNotLoadedWorkItemsFixEnabled
            ? getOrFetchSelectedItems(backlogContentService, backlogWorkItemService, currentItem)
            : getSelectedItems(backlogContentService, currentItem);
    }

    const assignedToItems = new ObservableArray<IMenuItem>();
    const selectedItems = getSelectedItemsCall();

    const onEditTemplate = async () => {
        let fields: IFieldEntry[] | undefined = [];
        if (useNewWorkItemFieldService) {
            fields = await workItemFieldService2?.loadFields()
        } else {
            fields = await workItemFieldService?.getFieldsAsync();
        }

        openDialog(backlogContentService, onDismiss => (
            <EditTemplateDialog
                fields={fields ?? []}
                onDismiss={onDismiss}
                onSave={() => loadTemplateMenuItems()}
                selectedItem={currentItem.underlyingItem.data.value.coreColumnMap}
            />
        ));
    };

    const templateMenuItems = new ObservableArray<IMenuItem>([
        {
            groupKey: "capture-template",
            id: "capture-work-item",
            onActivate: () => {
                onEditTemplate();
            },
            text: Resources.WorkItemTemplateCapture,
            iconProps: { iconName: "Camera" },
            disabled: selectedItems && selectedItems.length > 1
        }
    ]);
    let teamData: ITeamData | undefined = undefined;

    backlogWorkItemService.updateWorkItemsByIdIfWereReordered(selectedItems.map(item => item.value.id));

    const getTeamData = async () => {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        teamData = await contributionService.getDataAsync<ITeamData>("ms.vss-tfs-web.team-context-data-provider");
        loadTemplateMenuItems();
    };
    getTeamData();

    const onAssignItemClick = async (identity: IIdentity | undefined) => {
        const items = useNotLoadedWorkItemsFixEnabled ?
            await getOrFetchSelectedItems(backlogContentService, backlogWorkItemService, currentItem)
            : getSelectedItems(backlogContentService, currentItem);

        WorkItemUtils.onAssignToItemActivated(
            pageContext,
            () => items,
            identity ? convertIIdentityToWorkItemIdentityRef(pageContext, identity) : undefined,
            async workItems => {
                useBulkOperationsOptimization
                    ? await updateWorkItemsAndRemoveIfNecessary(workItems, items)
                    : backlogWorkItemService.updateWorkItems(
                        workItems,
                        items.map(i => i.value)
                    );
            },
            undefined /*getWorkItems*/,
            useProcessingDialogForLongBacklogOperations,
            backlogContentService.getTreeSelection()
        );
    };

    const unassignedItem = getUnassignedMenuItem(pageContext, () => { onAssignItemClick(undefined) });

    const addToAssignedTo = (identities: IIdentity[]) => {
        const assignedToItemsArray = identities.map<IMenuItem>(identity => ({
            id: identity.localId!,
            text: identity.displayName,
            iconProps: {
                render: () => <Persona size={PersonaSize.size24} identity={identity} role="presentation" />
            },
            onActivate: () => { onAssignItemClick(identity); }
        }));
        assignedToItems.splice(0, assignedToItems.length, unassignedItem, ...assignedToItemsArray);
    };

    const getIdentities = () => {
        const peopleProvider = new PeoplePickerProvider(pageContext, undefined, filterIdentities);
        const identities = peopleProvider.onEmptyInputFocus(/*maxItemsCount*/ 10);
        if (Array.isArray(identities)) {
            addToAssignedTo(identities);
        } else if (identities instanceof Promise) {
            const identitiesPromise = identities as Promise<IIdentity[]>;
            identitiesPromise.then((resolvedIdentities: IIdentity[]) => {
                addToAssignedTo(resolvedIdentities);
            });
        }
    };

    const filterIdentities = (entityList: IIdentity[]) => entityList && entityList.filter(entity => !!entity.localId);

    getIdentities();

    const loadTemplateMenuItems = async () => {
        const workItemType = currentItem.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType] as string;
        if (!teamData || !workItemType) {
            return;
        }

        const templateService = pageContext.getService<IWorkItemTemplateService>("IWorkItemTemplateService");
        const templates = await templateService.getMyTemplateReferences(projectId, teamData.id, workItemType);
        templateMenuItems.push(
            ...templates.map(template => ({
                id: template.id,
                onActivate: () => {
                    applyTemplate(template.id);
                },
                text: template.name
            }))
        );
    };

    const applyTemplate = async (templateId: string) => {
        const selectedItems = await getOrFetchSelectedItemsCall();
        if (!teamData) {
            return;
        }

        if (useNewWorkItemFieldService) {
            await workItemFieldService2?.loadFields()
        } else {
            await workItemFieldService?.getFieldsAsync();
        }

        const workItemIds = selectedItems.map(selectedItem => Number(selectedItem[CoreFieldRefNames.Id]));
        const templateService = pageContext.getService<IWorkItemTemplateService>("IWorkItemTemplateService");
        const template = await templateService.getTemplate(projectId, teamData.id, templateId);
        const changesList: IWorkItemFieldChange[] = [];
        for (const fieldName in template.fields) {
            let value: any = template.fields[fieldName];
            const field = useNewWorkItemFieldService
                ? workItemFieldService2?.getFieldByReferenceName(fieldName)
                : workItemFieldService?.getFields()?.find(field => field.referenceName === fieldName);
            if (field) {
                if (field.type === FieldType.DateTime) {
                    value = new Date(value);
                } else if (field.type === FieldType.Integer || field.type === FieldType.Boolean) {
                    value = Number(value);
                } else if (field.isIdentity && value) {
                    if (isMeMacro(value, false)) {
                        const currentUser = pageContext.getService<IVssPageService>("IVssPageService").getData()?.user;
                        value = currentUser ? getDistinctDisplayName(currentUser.displayName, currentUser.uniqueName) : value;
                    }
                    value = convertWorkItemIdentityRefFromFieldValue(pageContext, value);
                } else if (field.type === FieldType.History && isFeatureFlagEnabled(pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false)) {
                    value = convertHTMLCommentToMarkdown(value);
                }
            }

            changesList.push({ fieldName, value });
        }

        const changesMap = new Map<string, IWorkItemFieldChange[]>([[BulkUpdateConstants.AllTypes, changesList]]);
        if (useBulkOperationsOptimization) {
            await bulkUpdateWorkItems(pageContext, workItemIds, changesMap, {
                selection: backlogContentService.getTreeSelection(),
                useProcessDialog: useProcessingDialogForLongBacklogOperations,
                onModifyItems: async workItems => updateWorkItemsAndRemoveIfNecessary(workItems, selectedItems),
            });
        } else {
            const workItems = await performBulkUpdate(pageContext, workItemIds, changesMap);
            backlogWorkItemService.updateWorkItems(
                workItems,
                selectedItems.map(i => i.value)
            );
        }
    };

    const updateWorkItemsAndRemoveIfNecessary = async (workItems: IWorkItem[], items: ({ value: IBacklogTreeRow } & INamedFieldDataDictionary)[]): Promise<void> => {
        try {
            backlogContentService.setBulkBacklogOperationIsInProgress(useBulkOperationsOptimization);

            await backlogWorkItemService.updateWorkItems(
                workItems,
                items.map(i => i.value),
                useBulkOperationsOptimization
            );
            if (useBulkOperationsOptimization) {
                await backlogContentService.updateWorkItemRows(workItems.map(workItem => ({ workItemId: workItem.id, workItem: workItem })));
            }
            await removeWorkItemsIfNecessary(pageContext, workItems, hub);
        } finally {
            backlogContentService.setBulkBacklogOperationIsInProgress(false);
        }
    };

    // edit
    menuItems.push([
        {
            id: "edit",
            groupKey: "manage-work-item",
            rank: 1,
            text: Resources.Edit,
            iconProps: { ariaHidden: true, iconName: "Edit" },
            onActivate: async () => {
                const items = await getOrFetchSelectedItemsCall();

                let fields: IFieldEntry[] | undefined;
                if (useNewWorkItemFieldService) {
                    fields = await workItemFieldService2?.loadFields()
                } else {
                    fields = await workItemFieldService?.getFieldsAsync();
                }

                openDialog(backlogContentService, onDismiss => {
                    const selection = backlogContentService.getTreeSelection();
                    return (
                        <BulkEditDialog
                            fields={fields ?? []}
                            onDismiss={onDismiss}
                            selectedItems={items}
                            onModifyItems={async workItems => updateWorkItemsAndRemoveIfNecessary(workItems, items)}
                            selection={selection}
                            itemsPerPage={!useBulkOperationsOptimization ? 10 : undefined}
                        />
                    );
                });
            }
        }
    ]);

    // Assign to
    menuItems.push([
        {
            rank: 14,
            id: "assign-work-item",
            text: Resources.AssignTo,
            iconProps: { iconName: "People" },
            groupKey: "manage-work-item",
            subMenuProps: {
                id: "assign-work-item-submenu",
                items: assignedToItems
            }
        }
    ]);

    // Copy as HTML
    menuItems.push([
        WorkItemUtils.getCopyAsHTMLCommandMenuItem(
            pageContext,
            "manage-work-item",
            getOrFetchSelectedItemsCall,
            backlogContentService.getBacklogTree().value?.visibleColumns ?? []
        )
    ]);

    // Copy Link
    menuItems.push([
        WorkItemUtils.getCopyLinkCommandMenuItem(
            pageContext,
            "manage-work-item",
            () => selectedItems,
            selectedItems.length !== 1)]);

    // delete
    if (hasPermissionToDeleteWorkItem) {
        menuItems.push([
            {
                id: "delete",
                rank: 40,
                groupKey: "manage-work-item",
                text: Resources.Delete,
                iconProps: { ariaHidden: true, iconName: "Delete" },
                onActivate: async () => {
                    const items = await getOrFetchSelectedItemsCall();
                    openDeleteDialog(backlogContentService, backlogWorkItemService, planningPaneSvc, items);
                }
            }
        ]);
    }

    // Templates
    if (hub === HubType.Backlogs) {
        menuItems.push([
            {
                id: "templates",
                rank: 50,
                groupKey: "manage-work-item",
                text: Resources.WorkItemTemplates,
                iconProps: { iconName: "AutoFillTemplate" },
                subMenuProps: {
                    id: "work-item-templates-sub-menu",
                    items: templateMenuItems
                }
            }
        ]);
    }

    const useBacklogAddLinkPerfomanceOptimization = isFeatureFlagEnabled(
        pageContext,
        FeatureFlags.UseBacklogAddLinkPerfomanceOptimization,
        false
    );

    // Add links
    const projectId = getTFSProject(pageContext)?.id ?? "";
    const items = [];
    items.push(WorkItemUtils.getLinkToNewCommandMenuItem(
        pageContext,
        getOrFetchSelectedItemsCall,
        projectId));

    if (!useBacklogAddLinkPerfomanceOptimization) {
        items.push(WorkItemUtils.getLinkToExistingCommandMenuItemOld(
            pageContext,
            getOrFetchSelectedItemsCall));
    } else {
        items.push(WorkItemUtils.getLinkToExistingCommandMenuItem(
            pageContext,
            getOrFetchSelectedItemsCall,
            async (workItems, newLinks) => {
                if (!newLinks?.length) {
                    return;
                }

                try {
                    backlogContentService.setBulkBacklogOperationIsInProgress(true);

                    const selectionCopy = backlogContentService.getTreeSelection()
                        ? backlogContentService.getTreeSelection().value.map(item => ({ ...item }))
                        : [];

                    const workItemIds = workItems.map(item => item.id);

                    await new Promise(resolve => {
                        openDialog(backlogContentService, onInnerDismiss => {
                            return <ProcessWorkItemsDialog
                                onDismiss={() => {
                                    onInnerDismiss();
                                    resolve(undefined);
                                }}
                                workItemIds={workItemIds}
                                processWorkItems={async batchWorkItemIds => {
                                    const batchItems = workItems.filter(workItem => batchWorkItemIds.includes(workItem.id));
                                    for (const link of newLinks) {
                                        //link should return true for IsNew method in order to be saved
                                        //isNew method is dependant on AddedDate property
                                        link.linkData["Changed Date"] = undefined;
                                        link.linkData.AddedDate = undefined;
                                    }
                                    for (let workItem of batchItems) {
                                        workItem.addLinks(newLinks);
                                    }
                                    await workItemFormSavingService.saveWorkItemsBatchAsync(batchItems);
                                    return true;
                                }}
                                maximumAmountOfSimultaneousRequests={1}
                            />
                        });
                    });

                    const addedLinksInfo: ILinkInfo[] = newLinks.map(link => ({
                        command: LinkCommand.Add,
                        targetId: link.getTargetId(),
                        sourceId: workItemIds[0],
                        linkData: link.linkData
                    }));

                    if (addedLinksInfo && addedLinksInfo.some(link => shouldUpdateFromLink(link, backlogContentService))) {
                        await hubService.refreshTree({
                            keepOpen: true,
                            filterManager: backlogWorkItemService.getFilterManager()
                        })
                    } else {
                        await backlogContentService.updateWorkItemRows(workItems.map(workItem => ({ workItemId: workItem.id, workItem: workItem })));
                        await removeWorkItemsIfNecessary(pageContext, workItems, hub);

                        if (backlogContentService.getTreeSelection() && selectionCopy.length > 0) {
                            restoreSelection(selectionCopy, backlogContentService.getTreeSelection());
                        }
                    }
                    planningPaneSvc.planningItemProvider && planningPaneSvc.updateIterations();
                } finally {
                    backlogContentService.setBulkBacklogOperationIsInProgress(false);
                }
            }));
    }

    menuItems.push([
        {
            id: "add-links",
            rank: 10,
            groupKey: "manage-links-and-order",
            text: Resources.AddLink,
            subMenuProps: { items }
        }
    ]);

    // move to iteration
    if (agileBacklogPermission) {
        menuItems.push([
            {
                id: "move-to-iteration",
                rank: 15,
                groupKey: "manage-links-and-order",
                text: Resources.MoveToIteration,
                subMenuProps: { id: "iteration-sub-menu-items", items: iterationItems }
            }
        ]);

        // move to top
        const providerItems = backlogContentService.getSelectedProviderItems();
        const parentItems = providerItems && new Set(providerItems.map(item => item.parentItem?.underlyingItem.data ?? undefined));
        if (parentItems?.size === 1 && hub === HubType.Sprints && currentItem.underlyingItem.data.value.order !== 1) {
            menuItems.push([
                {
                    id: "move-to-top",
                    rank: 15,
                    groupKey: "manage-links-and-order",
                    text: Resources.MoveToTop,
                    onActivate: () => {
                        moveToTop(backlogContentService, backlogWorkItemService, pageContext);
                    }
                }
            ]);
        }

        // move to position
        if (currentItem.underlyingItem.data.value.order && hub === HubType.Backlogs) {
            menuItems.push([
                {
                    id: "move-to-position",
                    rank: 20,
                    groupKey: "manage-links-and-order",
                    iconProps: { iconName: "NumberedListText" },
                    text: Resources.MoveToPosition,
                    onActivate: () => {
                        openDialog(backlogContentService, onDismiss => {
                            const maximumOrder = backlogContentService.getBacklogTree().value?.rows.length ?? 1;
                            const providerItems = backlogContentService.getSelectedProviderItems(currentItem);
                            return (
                                <MoveToPositionDialog
                                    onDismiss={onDismiss}
                                    onReorder={position => {
                                        modifyItemsToUpdate(providerItems);
                                    }}
                                    maximumOrder={maximumOrder}
                                    selectedItems={providerItems}
                                    pageContext={pageContext}
                                />
                            );
                        });
                    }
                }
            ]);
        }
    }

    // Change type
    menuItems.push([
        WorkItemUtils.getChangeTypeCommandMenuItem(
            pageContext,
            "export",
            getOrFetchSelectedItemsCall,
            false,
            async workItems => {
                const items = getSelectedItemsCall();
                if (useBulkOperationsOptimization) {
                    await updateWorkItemsAndRemoveIfNecessary(workItems, items)
                } else {
                    backlogWorkItemService.updateWorkItems(
                        workItems,
                        items.map(i => i.value)
                    );
                }
            },
            projectId
        )
    ]);

    // move to project
    menuItems.push([
        WorkItemUtils.getMoveToTeamProjectCommandMenuItem(
            pageContext,
            "export",
            getOrFetchSelectedItemsCall,
            async workItems => {
                const items = getSelectedItemsCall();

                if (useBulkOperationsOptimization) {
                    await updateWorkItemsAndRemoveIfNecessary(workItems, items)
                } else {
                    backlogWorkItemService.updateWorkItems(
                        workItems,
                        items.map(i => i.value)
                    );
                    removeWorkItemsIfNecessary(pageContext, workItems, hub);
                }
            })
    ]);

    if (hub === HubType.Backlogs) {
        menuItems.push([
            {
                id: "changeParent",
                rank: 18,
                groupKey: "manage-links-and-order",
                text: Resources.ChangeParent,
                iconProps: { ariaHidden: true, iconName: "Org" },
                onActivate: async () => {
                    const items = await getOrFetchSelectedItemsCall();

                    openDialog(backlogContentService, onDismiss => {
                        const currentTeam = backlogContentService.getCurrentTeam();

                        return (
                            <ChangeParentPanel
                                onDismiss={onDismiss}
                                onChangeParent={(items: INamedFieldDataDictionary[], newParentId: number) => {
                                    const itemProvider = backlogContentService.getBacklogTree().value?.itemProvider.value;
                                    if (itemProvider) {
                                        const parentIndex = itemProvider.findIndex(row => {
                                            return row.underlyingItem.data.id === newParentId;
                                        });
                                        const workItemIds = items.map(value => value["id"] as number);

                                        if (parentIndex > c_unparentedItemIndex) {
                                            const reparentingWorkItems = workItemIds
                                            .map(workItemId =>
                                                itemProvider.find(row => {
                                                    return row.underlyingItem.data.id === workItemId;
                                                })
                                            )
                                            .filter((row): row is ITreeItemEx<IBacklogTreeRow> => {
                                                return !!row;
                                            });
                                            const parentItem = itemProvider[parentIndex].underlyingItem.data;
                                            backlogContentService.insertItems(reparentingWorkItems, undefined, parentIndex);

                                            backlogWorkItemService
                                                .reorderWorkItem(
                                                    items.map(value => value["id"] as number),
                                                    parentItem,
                                                    null, /*prevItemId*/
                                                    null, /*nextItemId*/
                                                    undefined, /*currentIteration*/
                                                    undefined, /*skipNextAndPrevIdsCheck*/
                                                    useChangeParentPerfomanceOptimization
                                                )
                                                .catch(e => {
                                                    getBoardsMessagesService(pageContext).showMessage(getErrorMessage(e));
                                                    backlogContentService.refreshTree();
                                                });
                                        } else {
                                            backlogWorkItemService
                                                .changeWorkItemParentLink(workItemIds, newParentId, useChangeParentPerfomanceOptimization)
                                                .then(() => {
                                                    hubService.refreshTree({
                                                        keepOpen: true,
                                                        filterManager: backlogWorkItemService.getFilterManager()
                                                    });
                                                })
                                                .catch(e => {
                                                    getBoardsMessagesService(pageContext).showMessage(getErrorMessage(e));
                                                });
                                        }
                                    }
                                }}
                                currentDirectory={{
                                    id: currentTeam.id,
                                    name: currentTeam.name,
                                    isFavorite: false,
                                    isMember: false,
                                    isMostRecent: false,
                                    url: ""
                                }}
                                directoryDropdownProvider={directoryDropdownProvider}
                                pageContext={backlogContentService.getPageContext()}
                                workItemsToChange={items}
                            />
                        );
                    });
                }
            }
        ]);
    }

    // Email
    menuItems.push([
        WorkItemUtils.getEmailCommandMenuItem(
            pageContext,
            backlogContentService.getBacklogTree().value!.visibleColumns,
            getOrFetchSelectedItemsCall)
    ]);

    // New branch
    menuItems.push([WorkItemUtils.getNewBranchCommandMenuItem(pageContext, getSelectedItemsCall)]);

    // contributed items:
    const backlogTree = backlogContentService.getBacklogTree().value;
    const treeProviderItems = backlogTree?.itemProvider.value;

    if (treeProviderItems && backlogTree) {
        const context: IBacklogMenuItemContributionContext = {
            hideDelete: false, // TODO: make this permissions based?
            immediateSave: true,
            refreshRequired: true,
            team: backlogContentService.getCurrentTeam(),
            workItemIds: [],
            workItemProject: {},
            workItemTypeNames: []
        };

        const processedWorkItemIds: { [key: number]: boolean } = {};

        function processWorkItem(item: IBacklogTreeRow) {
            if (item.id > c_unparentedItemIndex && item.value.columnValues.length > 0) {
                if (!processedWorkItemIds[item.id]) {
                    const workItemType = item.value.coreColumnMap[CoreFieldRefNames.WorkItemType] as string;
                    const project = item.value.coreColumnMap[CoreFieldRefNames.TeamProject] as string;
                    context.workItemIds?.push(item.id);
                    context.workItemProject![item.id] = project;
                    context.workItemTypeNames?.push(workItemType);
                    processedWorkItemIds[item.id] = true;
                }
            }
        }

        const selectedItems = backlogContentService.getSelectedItems(currentItem.underlyingItem.data);
        selectedItems.forEach(item => processWorkItem(item));

        const processExternalMenuItem = (menuItem: IMenuItem, contribution: IMenuItem | IExternalMenuItem) => {
            return menuItems.value.find(item => item.id === menuItem.id) ? undefined : menuItem;
        };

        menuItems.push(getMenuItems(pageContext, "ms.vss-work-web.backlog-item-menu", context, { processExternalMenuItem }, true));
        menuItems.push(getMenuItems(pageContext, "ms.vss-work-web.work-item-context-menu", context, { processExternalMenuItem }, true));
    }

    return menuItems;
}

function getHubService(pageContext: IVssPageContext, hub: HubType): IHubContentService {
    return hub === HubType.Backlogs
        ? pageContext.getService<IBacklogsHubContentService>("IBacklogsHubContentService")
        : pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
}

function isDraggable(
    selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[],
    hubService: IHubContentService,
    workItemTypeStatesAndCategories: { [workItemType: string]: WorkItemStateAndColor[] | undefined } | undefined
): boolean {
    if (hubService.isReorderDisabled) {
        return false;
    }

    for (const item of selectedProviderItems) {
        const stateValue = item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.State] as string | undefined | null;
        const workItemType = item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType] as string | undefined | null;

        if (workItemTypeStatesAndCategories && stateValue && workItemType) {
            const typeStates = workItemTypeStatesAndCategories[workItemType];
            const newState = typeStates?.find(itemState => equals(itemState.name, stateValue));
            if (newState?.category === WorkItemStateCategories.Removed) {
                return false;
            }
        }
    }

    return true;
}

function isDragOverable(
    event: BoltListDragEvent<HTMLElement, ITreeItemEx<IBacklogTreeRow>>,
    dropData: IListDropData,
    backlogContentService: ICommonBacklogContentService,
    backlogWorkItemService: IBacklogWorkItemService,
    isFiltering: boolean,
    keepHierarchyWithFilters?: IObservableValue<boolean>,
    itemsBlockedForReordering?: Set<number>,
    backlogReorderingDragnDropHierarchyFixEnabled?: boolean,
    useDepthCalulationMethodInsideDragOverableCheck?: boolean
): boolean {
    const ignoreViaFiltering = isFiltering && (!keepHierarchyWithFilters || keepHierarchyWithFilters.value === false);

    if (ignoreViaFiltering) {
        return false;
    }
    const treeProvider: ITreeItemEx<IBacklogTreeRow>[] | undefined = backlogContentService.getBacklogTree().value?.itemProvider.value;

    if (!treeProvider) {
        return true;
    }

    const selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[] = backlogContentService.getSelectedProviderItems(event.detail.dataTransfer.data);

    if (selectedProviderItems.some(item => item.underlyingItem.data.id <= 0 || itemsBlockedForReordering?.has(item.underlyingItem.data.id))) {
        // item is not reordarable or a folder is selected
        return false;
    }
    const lowestDepthProviderItems: ITreeItemEx<IBacklogTreeRow>[] = filterLowestDepthItems(selectedProviderItems);

    // If this is a child at the top of the tree
    if (dropData.index === 0 && lowestDepthProviderItems[0].depth !== 0) {
        return false;
    }

    const targetTreeItem = treeProvider.find((element, index) => {
        return index === dropData.index - 1;
    });

    if (!targetTreeItem) {
        return true;
    }

    const immediateParent: ITreeItemEx<IBacklogTreeRow> | undefined = targetTreeItem?.parentItem;

    // find parent that matches depth of item held
    let matchingParent = targetTreeItem;
    while (matchingParent?.depth !== lowestDepthProviderItems[0].depth) {
        if (matchingParent?.parentItem) {
            matchingParent = matchingParent.parentItem;
        } else {
            break;
        }
    }

    const matchingParentIndex = treeProvider.indexOf(matchingParent);

    let lastChild;
    if (matchingParent) {
        for (let i = matchingParentIndex + 1; i < treeProvider.length; i++) {
            if (treeProvider[i].depth <= matchingParent.depth) {
                lastChild = treeProvider[i - 1].underlyingItem;
                break;
            }

            if (i === treeProvider.length - 1) {
                lastChild = treeProvider[i].underlyingItem;
            }
        }
    }

    for (const item of selectedProviderItems) {
        const row = item.underlyingItem.data.value;
        const itemReorderable = backlogWorkItemService.isBacklogWorkItemReorderableAtAreaPath(
            row.coreColumnMap[backlogContentService.getTeamField() ?? CoreFieldRefNames.AreaPath] as string
        );

        let itemDepthToUse = item.depth;
        let targetTreeItemDepthToUse = targetTreeItem.depth;

        if (useDepthCalulationMethodInsideDragOverableCheck) {
            const itemWorkItemType = item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
            if (itemWorkItemType) {
                itemDepthToUse = backlogContentService.getWorkitemTypesMinDepth([itemWorkItemType]);
            }

            const targetItemWorkItemType = item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
            if (targetItemWorkItemType) {
                targetTreeItemDepthToUse = backlogContentService.getWorkitemTypesMinDepth([targetItemWorkItemType]);
            }
        }

        // if drag n drop on item with same depth
        if (itemDepthToUse === targetTreeItemDepthToUse && !itemReorderable) {
            // restrict moving across same parent
            if (
                matchingParent?.parentItem?.underlyingItem.data.id === item.parentItem?.underlyingItem.data.id ||
                targetTreeItemDepthToUse === treeProvider[dropData.index]?.depth
            ) {
                return false;
            }
        }

        const targetItemReorderableCheckValue = targetTreeItem.underlyingItem.data.value.coreColumnMap[
            backlogContentService.getTeamField() ?? CoreFieldRefNames.AreaPath
        ] as string;

        const targetItemReorderable = backlogWorkItemService.isBacklogWorkItemReorderableAtAreaPath(targetItemReorderableCheckValue);
        const targetIsNotUnparentedFolder = targetTreeItem.underlyingItem.data.id > c_unparentedItemIndex;

        // restrict drag n drop reorderable item after non-reorderable
        if (itemReorderable && targetIsNotUnparentedFolder && !targetItemReorderable && itemDepthToUse === targetTreeItemDepthToUse) {
            return false;
        }
    }

    if (useDepthCalulationMethodInsideDragOverableCheck) {
        return isReorderingAvailableWithDepthCalculation(
            backlogReorderingDragnDropHierarchyFixEnabled,
            selectedProviderItems,
            backlogContentService,
            targetTreeItem
        );
    }

    if (
        selectedProviderItems.find(item => item === immediateParent) || // Block dropping parent onto same spot its already at
        (targetTreeItem &&
            ((targetTreeItem.depth > lowestDepthProviderItems[0].depth && lastChild !== undefined && targetTreeItem.underlyingItem != lastChild) || // Block dropping a parent onto a child that is more than 1 depth apart
                (lowestDepthProviderItems[0].depth === immediateParent?.depth && // Unless their immediate parent is the same level as lowestDepthProviderItems[0].depth
                    targetTreeItem.underlyingItem != lastChild) || // And unless its the last child
                targetTreeItem.depth < lowestDepthProviderItems[0].depth - 1 || // Block dropping a child that's too low (can't drop User Story onto Epic Level)
                (targetTreeItem.underlyingItem.expanded &&
                    targetTreeItem.underlyingItem.childItems?.length &&
                    targetTreeItem.depth === lowestDepthProviderItems[0].depth))) // AND Block dropping same level on parent if parent is expanded
    ) {
        return isReorderingAvailableWithDepthCalculation(
            backlogReorderingDragnDropHierarchyFixEnabled,
            selectedProviderItems,
            backlogContentService,
            targetTreeItem
        );
    }

    return true;
}

function isReorderingAvailableWithDepthCalculation(
    backlogReorderingDragnDropHierarchyFixEnabled: boolean | undefined,
    selectedProviderItems: ITreeItemEx<IBacklogTreeRow>[],
    backlogContentService: ICommonBacklogContentService,
    targetTreeItem: ITreeItemEx<IBacklogTreeRow>): boolean {
    if (backlogReorderingDragnDropHierarchyFixEnabled) {
        const uniqueWorkItemTypes = getUniqueWorkItemTypesForSelectedItems(selectedProviderItems);
        const calculatedDepth = backlogContentService.getWorkitemTypesMinDepth(uniqueWorkItemTypes);

        const parentOfTargetItemWIType = targetTreeItem.parentItem?.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();

        const targetItemWorkItemType = targetTreeItem.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.WorkItemType]?.toString();
        const workItemTypeToUse = (uniqueWorkItemTypes.length > 1 || targetItemWorkItemType === uniqueWorkItemTypes[0])
            ? parentOfTargetItemWIType ?? targetItemWorkItemType
            : targetItemWorkItemType;

        let targetItemDepthToUse = targetTreeItem.depth; //unparented folder does not have workItemType

        if (workItemTypeToUse) {
            targetItemDepthToUse = backlogContentService.getWorkitemTypesMinDepth([workItemTypeToUse]);
        }

        const targetIsUnparentedFolder = targetTreeItem.underlyingItem.data.id <= c_unparentedItemIndex;
        if (targetIsUnparentedFolder) {
            return targetItemDepthToUse < calculatedDepth && (calculatedDepth - targetItemDepthToUse) <= 1;
        }

        return targetItemDepthToUse <= calculatedDepth && (calculatedDepth - targetItemDepthToUse) <= 1;
    }

    return false;
}

async function moveToTop(
    backlogContentService: ICommonBacklogContentService,
    backlogWorkItemService: IBacklogWorkItemService,
    pageContext: IVssPageContext
) {
    const providerItems = backlogContentService.getSelectedProviderItems();
    const rows = backlogContentService.getBacklogTree().value!.rows;

    if (!providerItems || providerItems.length === 0) {
        //sometimes tree is refreshed and selected items are lost
        console.error("Move to top can't be performed. No items selected");
        return;
    }

    const parentItems = new Set(providerItems.map(item => item.parentItem?.underlyingItem.data ?? undefined));
    if (parentItems.size > 1) {
        const renderMessage = () => Resources.MoveToTopWarning;

        getBoardsMessagesService(pageContext).showMessage({
            renderMessage: renderMessage,
            severity: MessageCardSeverity.Warning
        });
        return;
    }

    let nextId = 0;
    let firstPosition = 0;
    const parentItem: IBacklogTreeRow | undefined = parentItems.values().next().value;
    if (!parentItem) {
        nextId = rows[firstPosition].data.id;
        if (nextId === -1) {
            //unparented folder is at the top
            firstPosition++;
            nextId = rows[firstPosition].data.id;
        }
    } else {
        const itemProviderItems = backlogContentService.getBacklogTree().value?.itemProvider.value;

        //index of first element from itemProviderItems collection which parent is parentItem
        firstPosition = itemProviderItems?.findIndex(treeItem => treeItem.parentItem?.underlyingItem.data.id === parentItem.id) ?? -1;

        nextId = parentItem.children[0].id;
    }

    backlogContentService.insertItems(providerItems, undefined, firstPosition - 1);

    await backlogWorkItemService.reorderWorkItem(
        providerItems.map(item => item.underlyingItem.data.id),
        undefined,
        null,
        nextId
    );

    modifyItemsToUpdate(providerItems);
}

function modifyItemsToUpdate(providerItems: ITreeItemEx<IBacklogTreeRow>[]) {
    const selectedItems = providerItems.map(providerItem => providerItem.underlyingItem);
    selectedItems.forEach(item => itemsToUpdate.add(item.data.id));
}

function renderRollupCell(
    contentService: ICommonBacklogContentService,
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    const backlogItem = treeItem.underlyingItem.data.value;
    const columnValue = treeColumn.columnDetails.rollupCalculation
        ? contentService.getRollupData(Number(backlogItem.coreColumnMap[CoreFieldRefNames.Id]), treeColumn.columnDetails.rollupCalculation)
        : undefined;

    return (
        <SimpleTableCell className="backlog-tree-cell-rollup" columnIndex={columnIndex} key={`${rowIndex}-${columnIndex}`} tableColumn={treeColumn}>
            <WorkItemRollupProgressBar rollupData={columnValue} />
        </SimpleTableCell>
    );
}

// traversal backlog tree in DFS manner
function traverseBacklogTree(backlogTreeRow: IBacklogTreeRow, columnToSum: string, visited: Set<number>): number {
    // eliminate possible infinite loop
    if (visited.has(backlogTreeRow.id)) {
        return 0;
    }
    visited.add(backlogTreeRow.id);

    // sum current node value and children nodes values
    const sum = backlogTreeRow.children.reduce((sum, node) => {
        return sum + traverseBacklogTree(node, columnToSum, visited);
    }, (backlogTreeRow.value.coreColumnMap[columnToSum] as number) ?? 0);

    visited.delete(backlogTreeRow.id);
    return sum;
}

// entry point for traversing backlog tree
function backlogTreeColumnAggregation(backlogTreeRow: IBacklogTreeRow, columnToSum: string): number {
    return traverseBacklogTree(backlogTreeRow, columnToSum, new Set<number>());
}

function renderRemainingWorkCell(
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    return (
        <SimpleTableCell className="backlog-tree-cell" columnIndex={columnIndex} key={`${rowIndex}-${columnIndex}`} tableColumn={treeColumn}>
            {renderRemainingWorkCellContents(treeColumn, treeItem)}
        </SimpleTableCell>
    );
}

function renderRemainingWorkCellContents(treeColumn: IBacklogTreeColumn, treeItem: ITreeItemEx<IBacklogTreeRow>): JSX.Element {
    let columnElement: JSX.Element = <></>;
    const columnValue = backlogTreeColumnAggregation(treeItem.underlyingItem.data, treeColumn.columnDetails.name);
    if (columnValue) {
        columnElement = <div className="text-ellipsis">{columnValue.toLocaleString(undefined, DefaultNumberFormatOptions)}</div>;
    }

    return <Tooltip overflowOnly={true}>{columnElement}</Tooltip>;
}

function renderCell(rowIndex: number, columnIndex: number, treeColumn: IBacklogTreeColumn, treeItem: ITreeItemEx<IBacklogTreeRow>): JSX.Element {
    return (
        <SimpleTableCell className="backlog-tree-cell" columnIndex={columnIndex} key={`${rowIndex}-${columnIndex}`} tableColumn={treeColumn}>
            {renderCellContents(treeColumn, treeItem)}
        </SimpleTableCell>
    );
}

function renderCellContents(treeColumn: IBacklogTreeColumn, treeItem: ITreeItemEx<IBacklogTreeRow>): JSX.Element {
    const backlogItem = treeItem.underlyingItem.data.value;
    let columnValue = backlogItem.columnValues[treeColumn.columnIndex];
    let columnElement: JSX.Element = <></>;
    let overflowOnly = true;
    let tooltipText: string | undefined;

    switch (treeColumn.columnDetails.fieldType) {
        case FieldType.DateTime:
            if (columnValue !== undefined && columnValue !== null) {
                columnElement = (
                    <time className="text-ellipsis">{localeDateFormat(getDateInUserTimeZone(columnValue as Date), "g", shortFormat)}</time>
                );
            }
            break;
        case FieldType.Double:
            if (columnValue !== undefined && columnValue !== null) {
                columnElement = <div className="text-ellipsis">{columnValue.toLocaleString(undefined, DefaultNumberFormatOptions)}</div>;
            }
            break;
        case FieldType.Boolean:
            if (columnValue !== undefined && columnValue !== null) {
                columnElement = <div className="text-ellipsis">{columnValue ? Resources.TrueString : Resources.FalseString}</div>;
            }
            break;
        case FieldType.Html:
            if (columnValue !== undefined && columnValue !== null) {
                columnValue = htmlDecode(columnValue.toString());
                columnElement = <div className="text-ellipsis">{columnValue}</div>;
            }
            break;

        default:
            // We dont want the full identity string, we want to trim the email and only
            // include it in the tooltip.
            if (treeColumn.columnDetails.isIdentity && columnValue) {
                const lessSymbolPosition = (columnValue as string).indexOf("<");
                columnValue = lessSymbolPosition > 0 ? (columnValue as string).substring(0, lessSymbolPosition - 1) : (columnValue as string);
                tooltipText = backlogItem.columnValues[treeColumn.columnIndex] as string;
                overflowOnly = false;
            }

            // render the ID for rows that haven't been loaded yet
            if (!columnValue && equals(treeColumn.id, CoreFieldRefNames.Id)) {
                columnValue = treeItem.underlyingItem.data.id;
            }

            columnElement = <div className="text-ellipsis">{columnValue}</div>;
            break;
    }

    return (
        <Tooltip overflowOnly={overflowOnly} text={tooltipText}>
            {columnElement}
        </Tooltip>
    );
}

function expandShallowestLevel(itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined): void {
    if (!itemProvider) {
        return;
    }
    let itemsCurrentLevel = [...itemProvider.roots];
    let itemsNextLevel: ITreeItem<IBacklogTreeRow>[] = [];
    let itemsToCollapse: ITreeItem<IBacklogTreeRow>[] = [];

    while (itemsCurrentLevel.length > 0) {
        const item = itemsCurrentLevel.pop();

        if (item && !item.expanded) {
            itemsCurrentLevel.push(item);
            break;
        } else {
            if (item && item.childItems) {
                itemsNextLevel = itemsNextLevel.concat(item.childItems);
            }
        }
        if (itemsCurrentLevel.length === 0 && itemsNextLevel.length > 0) {
            itemsCurrentLevel = itemsNextLevel;
            itemsNextLevel = [];
        }
    }

    // Expand the items by one level and collapse their immediate children
    itemsCurrentLevel.forEach(item => {
        if (!item.expanded) {
            itemProvider.toggle(item);
            if (item.childItems) {
                itemsToCollapse = itemsToCollapse.concat(item.childItems);
            }
        }
    });

    // Collapse direct children of recently expanded items
    itemsToCollapse.forEach(item => {
        if (item.expanded) {
            itemProvider.toggle(item);
        }
    });
}

function collapseDeepestLevel(itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined): void {
    if (!itemProvider) {
        return;
    }
    let itemsCurrentLevel = [...itemProvider.roots];
    let itemsNextLevel: ITreeItem<IBacklogTreeRow>[] = [];
    let itemsPrevLevel: ITreeItem<IBacklogTreeRow>[] = [];
    let itemsCurrentLevelEvaluated: ITreeItem<IBacklogTreeRow>[] = [];

    while (itemsCurrentLevel.length > 0) {
        const item = itemsCurrentLevel.pop();

        if (item?.childItems && item.childItems?.length > 0 && item?.expanded) {
            itemsNextLevel = itemsNextLevel.concat(item.childItems);
            itemsCurrentLevelEvaluated.push(item);
        }

        if (itemsCurrentLevel.length === 0) {
            if (itemsNextLevel.length > 0) {
                itemsPrevLevel = itemsCurrentLevelEvaluated;
                itemsCurrentLevel = itemsNextLevel;
                itemsNextLevel = itemsCurrentLevelEvaluated = [];
            } else {
                break;
            }
        }
    }

    itemsPrevLevel.forEach(item => {
        if (item.expanded) {
            itemProvider.toggle(item);
        }
    });
}

function selectAllItems(pageContext: IVssPageContext): void {
    const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    contentService.selectAllItems();
}

function renderHeaderCell(pageContext: IVssPageContext, columnIndex: number, tableColumn: IBacklogTreeColumn): JSX.Element {
    const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const itemProvider: TreeItemProvider<IBacklogTreeRow> | undefined = contentService.getBacklogTree().value?.itemProvider;

    return (
        <TableHeaderCell key={"table-header" + columnIndex} column={tableColumn} columnIndex={columnIndex}>
            <div className="flex-row flex-start">
                <div className="backlogs-view-horizontal-padding">
                    <Button
                        ariaLabel={Resources.ExpandOneLevel}
                        className="expand-collapse-button"
                        iconProps={{ iconName: "Add", tooltipProps: { text: Resources.ExpandOneLevel }, size: IconSize.small }}
                        onClick={() => expandShallowestLevel(itemProvider)}
                    />
                </div>
                <div className="backlogs-view-horizontal-padding">
                    <Button
                        ariaLabel={Resources.CollapseOneLevel}
                        className="expand-collapse-button"
                        iconProps={{ iconName: "Remove", tooltipProps: { text: Resources.CollapseOneLevel }, size: IconSize.small }}
                        onClick={() => {
                            collapseDeepestLevel(itemProvider);
                        }}
                    />
                </div>
            </div>
        </TableHeaderCell>
    );
}

function renderRollupHeaderCell(pageContext: IVssPageContext, columnIndex: number, tableColumn: IBacklogTreeColumn): JSX.Element {
    const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const backlogTree = contentService.getBacklogTree().value;
    const name = tableColumn.name;
    let isRollupLoaded = true;
    if (backlogTree) {
        isRollupLoaded = backlogTree.rows.findIndex(col => col.data.value.columnValues[tableColumn.columnIndex] instanceof ObservableValue) !== -1;
    }

    return (
        <TableHeaderCell key={"table-header" + columnIndex} column={tableColumn} columnIndex={columnIndex}>
            <Tooltip text={name}>
                <div className="text-ellipsis body-s">
                    {!isRollupLoaded ? (
                        <Icon
                            ariaLabel={Resources.Warning}
                            className={"icon-margin"}
                            iconName="Warning"
                            tooltipProps={{ text: Resources.RollupHeaderFailure }}
                        />
                    ) : (
                        <Icon iconName="RowsChild" className="icon-margin" />
                    )}
                    <span className="bolt-table-header-cell-text text-ellipsis">{name}</span>
                </div>
            </Tooltip>
        </TableHeaderCell>
    );
}

function renderAddCell(
    pageContext: IVssPageContext,
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    const workItemTypes = treeItem.underlyingItem.data.value.childWorkItemTypes;
    const menuItems: IMenuItem[] = [];
    const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

    let tooltip = "";
    if (workItemTypes) {
        const names = workItemTypes.map(item => item?.workItemTypeName);
        const typeString = names.join(", ");
        tooltip = format(Resources.AddItem, typeString);

        if (workItemTypes.length > 1) {
            workItemTypes.map((value: WorkItemTypeColorAndIcon, index: number) => {
                menuItems[index] = {
                    id: `${index}`,
                    text: `${names[index]}`,
                    iconProps: {
                        render: () => <WorkItemTypeIcon className="icon-margin" data={value} />
                    },
                    onActivate: () => {
                        renderMenuItem(pageContext, layoutManager, value.workItemTypeName, contentService, treeItem);
                    }
                };
            });
        }
    }

    let cellContents;
    if (workItemTypes && workItemTypes.length > 1) {
        cellContents = (
            <MoreButton
                ariaLabel={tooltip}
                className="bolt-list-cell-content-reveal"
                buttonClassName="add-line-button"
                iconProps={{ iconName: "Add", size: IconSize.small }}
                subtle
                tooltipProps={{ text: tooltip }}
                contextualMenuProps={{
                    menuProps: {
                        id: "backlogs-work-item-dialogs",
                        items: menuItems
                    }
                }}
            />
        );
    } else if (workItemTypes && workItemTypes.length == 1) {
        cellContents = (
            <Button
                ariaLabel={tooltip}
                className="add-line-button bolt-list-cell-content-reveal"
                iconProps={{ iconName: "Add", size: IconSize.small }}
                subtle
                tooltipProps={{ text: tooltip }}
                onClick={() => renderMenuItem(pageContext, layoutManager, workItemTypes[0]?.workItemTypeName ?? "", contentService, treeItem)}
            />
        );
    }

    return (
        <SimpleTableCell
            className="justify-center remove-padding add-cell"
            columnIndex={columnIndex}
            tableColumn={treeColumn}
            key={`${rowIndex}-${columnIndex}`}
        >
            {!treeItem.underlyingItem.data.value.isValid && (
                <Icon ariaLabel={Resources.AlertIcon} className={"icon-large-margin error-text font-weight-semibold"} iconName="AlertSolid" />
            )}
            {cellContents}
        </SimpleTableCell>
    );
}

function renderForecastCell(
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    return (
        <SimpleTableCell className="backlog-tree-cell" columnIndex={columnIndex} ariaRowIndex={rowIndex} key={`${rowIndex}-${columnIndex}`}>
            <span className="text-ellipsis backlogs-forecast-primary-text font-weight-heavy">{treeItem.underlyingItem.data.iteration}</span>
        </SimpleTableCell>
    );
}

function renderOrderHeaderCell(
    columnIndex: number,
    tableColumn: ITableColumn<ITreeItemEx<IBacklogTreeRow>>,
    focuszoneId?: string | undefined,
    isFirstActionableHeader?: boolean | undefined
): JSX.Element {
    return (
        <TableHeaderCell
            key={"col-header-" + columnIndex}
            column={tableColumn}
            columnIndex={columnIndex}
            focuszoneId={focuszoneId}
            isFirstActionableHeader={isFirstActionableHeader}
        >
            <Tooltip overflowOnly={true} text={tableColumn.name}>
                <div className="bolt-table-header-cell-text text-ellipsis body-s flex">
                    <span>{tableColumn.name}</span>
                    {tableColumn.iconProps && Icon(tableColumn.iconProps)}
                </div>
            </Tooltip>
        </TableHeaderCell>
    );
}

function renderOrderCell(
    backlogContentService: ICommonBacklogContentService,
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    const row = treeItem.underlyingItem.data.value;
    const icon = backlogContentService.teamOwnsWorkItem(
        row.coreColumnMap[backlogContentService.getTeamField() ?? CoreFieldRefNames.AreaPath] as string
    ) ? undefined : (
        <Icon className={"backlogs-cannot-reorder-icon"} iconName="Info" tooltipProps={{ text: Resources.CannotReorder }} />
    );
    return (
        <SimpleTableCell
            className="backlog-tree-cell flex-row flex-center"
            columnIndex={columnIndex}
            key={`${rowIndex}-${columnIndex}`}
            tableColumn={treeColumn}
        >
            {row.order && <span className={"margin-right-4"}>{row.order}</span>}
            {icon}
        </SimpleTableCell>
    );
}

function renderStateCell(rowIndex: number, columnIndex: number, treeColumn: IBacklogTreeColumn, treeItem: ITreeItemEx<IBacklogTreeRow>): JSX.Element {
    const backlogItem = treeItem.underlyingItem.data.value;

    return (
        <SimpleTableCell className="backlog-tree-cell" columnIndex={columnIndex} key={`${rowIndex}-${columnIndex}`} tableColumn={treeColumn}>
            {backlogItem && (
                <React.Fragment>
                    {backlogItem.workItemStateAndColor && <WorkItemState data={backlogItem.workItemStateAndColor} hideText={false} />}
                </React.Fragment>
            )}
        </SimpleTableCell>
    );
}

function renderTagsCell(rowIndex: number, columnIndex: number, treeColumn: IBacklogTreeColumn, treeItem: ITreeItemEx<IBacklogTreeRow>): JSX.Element {
    const backlogItem = treeItem.underlyingItem.data.value;
    const systemLabel = backlogItem.columnValues[treeColumn.columnIndex] as string | undefined;
    let children: JSX.Element | undefined;

    if (systemLabel) {
        const tags = systemLabel.split(";");
        children = <TagsList className={"tag-item"} tags={tags} />;
    }

    return (
        <SimpleTableCell
            className="backlog-tree-cell backlog-tags-cell scroll-hidden"
            columnIndex={columnIndex}
            key={`${rowIndex}-${columnIndex}`}
            tableColumn={treeColumn}
        >
            {children}
        </SimpleTableCell>
    );
}

function renderParentCell(
    pageContext: IVssPageContext,
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    const parentItemId = treeItem.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.Parent] as string | undefined;
    if (!parentItemId) {
        return <SimpleTableCell columnIndex={columnIndex} key={"title" + rowIndex} />;
    }
    const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const parentItem = backlogContentService.getParentWorkItem(Number(parentItemId));
    const workItemTrackingDataProvider = new WorkItemTrackingDataProvider(pageContext);

    if (!parentItem) {
        return <SimpleTableCell columnIndex={columnIndex} key={"title" + rowIndex} />;
    }

    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    return (
        <SimpleTableCell columnIndex={columnIndex} key={"title" + rowIndex}>
            <Observer parentItem={parentItem}>
                {(observerProps: { parentItem: IWorkItem | undefined }) => {
                    return !observerProps.parentItem ? (
                        <div className="shimmer shimmer-line" style={{ width: Math.random() * 80 + 20 + "%" }}>
                            &nbsp;
                        </div>
                    ) : (
                        <div className="flex-row flex-grow scroll-hidden">
                            <WorkItemTypeIconProvider
                                dataProvider={workItemTrackingDataProvider}
                                dataSource={{
                                    projectIdOrName: observerProps.parentItem.getFieldValueByName(CoreFieldRefNames.TeamProject) ?? "",
                                    workItemTypeName: observerProps.parentItem.getFieldValueByName(CoreFieldRefNames.WorkItemType) ?? ""
                                }}
                            >
                                {icon => <WorkItemTypeIcon data={icon} className="icon-margin font-size-m" />}
                            </WorkItemTypeIconProvider>
                            <Tooltip overflowOnly={true}>
                                <Link
                                    className="bolt-list-cell bolt-table-inline-link text-ellipsis"
                                    excludeTabStop={true}
                                    href={locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
                                        id: String(parentItemId),
                                        project: getTFSPageData(pageContext)?.project?.name ?? ""
                                    })}
                                    onClick={e => {
                                        if (!e.ctrlKey && !e.shiftKey && (e as any).button !== 1) {
                                            openWorkItemDialog(pageContext, treeItem.underlyingItem.data, true);
                                            e.stopPropagation();
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    {observerProps.parentItem.getFieldValueByName(CoreFieldRefNames.Title)}
                                </Link>
                            </Tooltip>
                        </div>
                    );
                }}
            </Observer>
        </SimpleTableCell>
    );
}

function renderTitleCell(
    pageContext: IVssPageContext,
    rowIndex: number,
    columnIndex: number,
    treeColumn: IBacklogTreeColumn,
    treeItem: ITreeItemEx<IBacklogTreeRow>
): JSX.Element {
    const backlogItem = treeItem.underlyingItem.data.value;
    const workItemId = Number(backlogItem.coreColumnMap[CoreFieldRefNames.Id]);
    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
    const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    return (
        <ExpandableTreeCell
            className="backlog-tree-cell"
            contentClassName="bolt-table-cell-content-with-link"
            columnIndex={columnIndex}
            key={columnIndex}
            treeColumn={treeColumn}
            treeItem={treeItem}
        >
            {backlogItem.workItemTypeColorAndIcon && <WorkItemTypeIcon className="margin-right-4" data={backlogItem.workItemTypeColorAndIcon} />}
            <Tooltip overflowOnly={true}>
                <Link
                    className="bolt-list-cell bolt-table-inline-link text-ellipsis"
                    excludeTabStop={true}
                    href={locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
                        id: String(workItemId),
                        project: getTFSPageData(pageContext)?.project?.name ?? ""
                    })}
                    onClick={e => {
                        if (!e.ctrlKey && !e.shiftKey && (e as any).button !== 1) {
                            backlogContentService.getTreeSelection().select(rowIndex, 1);
                            openWorkItemDialog(pageContext, treeItem.underlyingItem.data);
                            e.stopPropagation();
                            e.preventDefault();
                        }
                    }}
                    removeUnderline={true}
                >
                    {backlogItem.columnValues[treeColumn.columnIndex]}
                </Link>
            </Tooltip>
        </ExpandableTreeCell>
    );
}

function openWorkItemDialog(pageContext: IVssPageContext, workItem: IBacklogTreeRow, openParentItem?: boolean) {
    const workItemFormDialogService = pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
    const backlogWorkItemService = pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
    const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");

    const workItemId = openParentItem ? Number(workItem.value.coreColumnMap[CoreFieldRefNames.Parent]) : workItem.id;
    const onDelete = openParentItem
        ? () => {
            // update all visible work item rows with this parent
            const childItems = backlogContentService
                .getBacklogTree()
                .value?.itemProvider.value.filter(item => item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.Parent] === workItemId);
            childItems && childItems.forEach(item => backlogContentService.updateWorkItemRow(item.underlyingItem.data.id, undefined, true));
        }
        : () => {
            backlogWorkItemService.deleteWorkItem([workItem]);
            backlogContentService.resetOrderColumn();
            backlogContentService.getTreeSelection().clear();
        };

    workItemFormDialogService.showWorkItemFormDialog(workItemId, undefined, {
        updateHistory: true,
        shouldRefresh: itemsToUpdate.has(workItemId),
        onDelete: onDelete
    });

    itemsToUpdate.delete(workItemId);
}

async function removeWorkItemsIfNecessary(pageContext: IVssPageContext, workItems: IWorkItem[], hub: HubType) {
    const getTreeItem = (workItem: IWorkItem) => {
        const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        const backlogTree = backlogContentService.getBacklogTree().value!;
        return backlogTree.itemProvider.value.find(row => row.underlyingItem.data.id === workItem.id);
    };

    const treeItems: ITreeItemEx<IBacklogTreeRow>[] = [];
    if (hub === HubType.Sprints) {
        const sprintsHubService = pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
        const currentIteration = sprintsHubService.getCurrentIteration();
        workItems.forEach(workItem => {
            if (
                currentIteration?.id !== workItem.getFieldValueByName(CoreFieldRefNames.IterationId) &&
                (!currentIteration || !workItem.getFieldValueByName(CoreFieldRefNames.IterationPath)?.startsWith(currentIteration.friendlyPath))
            ) {
                const treeItem = getTreeItem(workItem);
                treeItem && treeItems.push(treeItem);
            }
        });
        sprintsHubService.onIterationChange(treeItems);
    } else if (hub === HubType.Backlogs) {
        const backlogWorkItemService = pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        const backlogLevels = await backlogContentService.getAllBacklogLevels();
        workItems.forEach(async workItem => {
            if (!backlogContentService.teamOwnsWorkItem(workItem.getFieldValueByName(CoreFieldRefNames.AreaPath) as unknown as string)) {
                const levelWorkItemTypes = backlogLevels.find(
                    level => level.id === backlogContentService.getBacklogLevelId() || level.name === backlogContentService.getBacklogLevelId()
                )?.workItemTypes;
                if (levelWorkItemTypes?.find(type => type.name === workItem.getFieldValueByName(CoreFieldRefNames.WorkItemType))) {
                    const treeItem = getTreeItem(workItem);
                    treeItem && treeItems.push(treeItem);
                }
            }
        });

        backlogWorkItemService.updateBacklogTreeRowsDelete(treeItems);
        backlogWorkItemService.getFilterManager().dataUpdated();
        backlogContentService.resetOrderColumn();
    }
}

function renderRow(
    pageContext: IVssPageContext,
    rowIndex: number,
    treeItem: ITreeItemEx<IBacklogTreeRow>,
    details: ITreeRowDetails<IBacklogTreeRow>,
    columns: ITreeColumn<IBacklogTreeRow>[]
): JSX.Element {
    const backlogTreeRow = treeItem.underlyingItem.data;
    if (backlogTreeRow.id > c_unparentedItemIndex || backlogTreeRow.value.backlogLevel === undefined) {
        if (backlogTreeRow.value.columnValues.length === 0 || !backlogTreeRow.value.coreColumnMap[CoreFieldRefNames.Id]) {
            return <LoadingRow columns={columns} details={details} pageContext={pageContext} rowIndex={rowIndex} treeItem={treeItem} />;
        }
        return renderTreeRow(
            rowIndex,
            treeItem,
            details,
            columns,
            treeItem.underlyingItem.data,
            css(
                treeItem.underlyingItem.highlighted && "font-weight-heavy",
                !treeItem.underlyingItem.data.value.isValid && "font-weight-semibold error-text-important",
                treeItem.underlyingItem.data.iteration && "backlogs-forecast-row-underline"
            ),
            treeItem.underlyingItem.data.id
        );
    }
    return renderUnparentedRow(rowIndex, treeItem, details, columns);
}

function shouldUpdateFromLink(link: ILinkInfo, backlogContentService: ICommonBacklogContentService): boolean {
    const backlogTree = backlogContentService.getBacklogTree().value;
    if (!backlogTree) {
        return false;
    }
    const workItemIds = Object.keys(backlogTree.allRows);

    let parentId = 0;
    let childId = 0;
    if (link.linkData?.LinkType === c_parentLinkType) {
        parentId = link.targetId!;
        childId = link.sourceId!;
    } else if (link.linkData?.LinkType === c_childLinkType) {
        parentId = link.sourceId!;
        childId = link.targetId!;
    }

    if (!parentId) {
        return false;
    }
    // parent is visible and we aren't processing a change that's already been handled, i.e. drag and drop
    const childIds = backlogTree.allRows[parentId]?.children.flat().map(child => child.id);
    return (
        workItemIds.findIndex(id => Number(id) === parentId) >= 0 &&
        !(link.command === "add" && contains(childIds, childId)) &&
        !(link.command === "remove" && !contains(childIds, childId))
    );
}

interface ILoadingRowProps {
    columns: ITreeColumn<IBacklogTreeRow>[];
    details: ITreeRowDetails<IBacklogTreeRow>;
    pageContext: IVssPageContext;
    rowIndex: number;
    treeItem: ITreeItemEx<IBacklogTreeRow>;
}

function LoadingRow(props: ILoadingRowProps): JSX.Element {
    const { columns, details, pageContext, rowIndex, treeItem } = props;

    // attempt to load the data for this row on first render
    // it's possible to have an unloaded root item, which is why we do this here
    // even though we also fetch any unloaded children when a row is expanded
    // it's not a problem to attempt a double-fetch those child rows
    React.useEffect(() => {
        const backlogWorkItemService = pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");

        // only fetch existing work items
        if (treeItem.underlyingItem.data.id > 0) {
            backlogWorkItemService.fetchWorkItem(treeItem.underlyingItem.data.id);
        }
    }, [treeItem]);

    return (
        // copied from Tree.tsx
        <TreeRow index={rowIndex} details={details}>
            {columns.map((treeColumn: ITreeColumn<IBacklogTreeRow>, columnIndex: number) => {
                if (equals(treeColumn.id, CoreFieldRefNames.Id) && Number(treeColumn.id) > 0) {
                    return renderCell(rowIndex, columnIndex, treeColumn as IBacklogTreeColumn, treeItem);
                }
                const children = renderLoadingCell(treeColumn.columnLayout);
                return SimpleTableCell({ className: "bolt-tree-cell", columnIndex, children });
            })}
        </TreeRow>
    );
}

function renderUnparentedRow(
    rowIndex: number,
    treeItem: ITreeItemEx<IBacklogUnparentedItemFolder>,
    details: ITreeRowDetails<IBacklogUnparentedItemFolder>,
    columns: ITreeColumn<IBacklogTreeRow>[]
): JSX.Element {
    let folderName = Resources.UnparentedItems;
    if (treeItem.underlyingItem.data.value.backlogLevel) {
        folderName = format(Resources.UnparentedFolderName, treeItem.underlyingItem.data.value.backlogLevel);
    }
    return (
        <TreeRow key={rowIndex} index={rowIndex} details={details}>
            {columns.map((treeColumn: ITreeColumn<IBacklogTreeRow>, columnIndex: number) => {
                if (equals(treeColumn.id, CoreFieldRefNames.Title)) {
                    return (
                        <ExpandableTreeCell
                            className="backlog-tree-cell"
                            contentClassName="bolt-table-cell-content-with-link"
                            columnIndex={columnIndex}
                            key={columnIndex}
                            treeColumn={treeColumn}
                            treeItem={treeItem}
                        >
                            {Icon({
                                className: "backlogs-unparented-item padding-right-4",
                                iconName: "FabricFolderFill"
                            })}
                            <Tooltip overflowOnly={true}>
                                <div className="text-ellipsis">{folderName}</div>
                            </Tooltip>
                        </ExpandableTreeCell>
                    );
                }

                return renderEmptyCell(rowIndex, columnIndex);
            })}
        </TreeRow>
    );
}

function everyChildHasParent(selectedItems: ITreeItemEx<IBacklogTreeRow>[]): boolean {
    const lowestDepthProviderItems = filterLowestDepthItems(selectedItems);

    for (const item of selectedItems) {
        if (item.depth === lowestDepthProviderItems[0].depth) {
            continue;
        }

        let parent: ITreeItemEx<IBacklogTreeRow> | undefined = item.parentItem;

        while (parent && parent.depth > lowestDepthProviderItems[0].depth) {
            parent = parent.parentItem;
        }

        if (!parent || !lowestDepthProviderItems.includes(parent)) {
            return false;
        }
    }

    return true;
}

function updateForecastLines(hubService: IHubContentService, forecastSettings: IObservableForecastSettings) {
    (hubService as IBacklogsHubContentService).updateForecastVelocityAndLines(forecastSettings.velocity.value);
}

interface IBacklogUnparentedItemFolder extends IBacklogTreeRow {
    renderRow?: TreeRowRenderer<IBacklogUnparentedItemFolder>;
}

interface IBacklogMenuItemContributionContext {
    hideDelete?: boolean;
    immediateSave?: boolean;
    refreshRequired?: boolean;
    team?: ITeam;
    workItemIds?: number[];
    workItemProject?: { [workItemId: number]: string };
    workItemTypeNames?: string[];
}

interface IColumnDetails {
    treeColumns: ITreeColumn<IBacklogTreeRow>[];
    titleColumnIndex: number;
}

const shortFormat = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
});
